/**
 * MeGenInterface — state + event handlers + API layer ของโมดูล MeGen ทั้งหมด
 *
 * การต่อสาย (สิ่งที่ไฟล์นี้ไฟล์เดียวไม่ได้บอก):
 * - หน้าจอยิง `onEvent({ message, params })` → `src/HIS/MainHISController.tsx` switch (เขียน case มือทุกตัว)
 *   → handler ในไฟล์นี้ → `controller.setState`
 *   ⚠ exhaustive check ใน MainHISController ถูก comment ทิ้ง ⇒ ลืมเติม case แล้ว compiler ไม่ฟ้อง event จะตกลง default เงียบ ๆ
 * - ทางลัดที่ยังใช้อยู่: `CardMeGenMain.tsx` เขียน state ตรงด้วย `setProp("MeGenSequence", …)` 5 จุด และ
 *   `setProp("MeGenSequence.isGenerating", …)` อีก 3 จุด ⇒ `currentView`/`isGenerating` มีเจ้าของ 2 ทาง
 * - `MainHIS.tsx` render การ์ดนี้ด้วย `keepMounted: true` ⇒ **สลับแท็บแล้วโมดูลไม่ unmount** polling ใน useRunPolling ยังวิ่งต่อ
 *
 * ค่าที่มาจากข้างนอกโดยไฟล์นี้ไม่ได้ตั้งเอง:
 * - `controller.data.division` มาจาก cookie `division_id` ที่ `src/HIS/MainHIS.tsx` (`parseInt(... || "0")` ⇒ ไม่มี cookie = 0)
 *   และถูกเขียนทับเมื่อผู้ใช้เปลี่ยน division (`src/HIS/MainHISInterface.ts`) — โมดูลนี้ส่งต่อไป backend
 *   เป็น `his_division` (start run / generate scene / cost check) และเป็น `extra.division` ของ SDK
 * - `API_URL = CONFIG.PODCAST_API_URL as string` ประเมินตอน import (side effect) และ `as string` กลบกรณีค่าว่าง
 *   ⇒ ถ้าไม่ได้ตั้ง config ทุก request จะยิงเป็น relative path โดยไม่มีใครเตือน
 *
 * ลำดับที่บังคับ:
 * - `HandleStartMeGen` เป็น bootstrap ต้องถูกเรียกก่อน เพราะเป็น **ที่เดียว** ที่โหลด `mediaStyleList` /
 *   `voiceList` / `imageModelCatalog` / `videoModelCatalog` (สี่ handler นี้ไม่มี case ใน controller ⇒ หน้าจอยิงเองไม่ได้
 *   และมันกลืน error เงียบ ๆ) · `youtubeStatus` โหลดซ้ำได้ผ่าน event `HandleGetYouTubeStatus`
 *   (`CardMeGenMain` ยิงเอง, error แค่ console.warn) · `allOaUsersList` ถูก `HandleGetAiUsageRecords`
 *   โหลดให้เองถ้ายังว่าง ⇒ หน้า MeGen Cost ทำงานได้แม้ไม่ผ่าน bootstrap
 *   หมายเหตุ: `getSchedule` + `searchOaUsers` ถูก await ผ่าน `Promise.allSettled` ส่วนที่เหลือเป็น fire-and-forget
 *
 * convention ของ error (ตอนนี้ยังไม่สม่ำเสมอ — ของใหม่ให้ใช้แบบแรก):
 * - ปกติ: `setErrorMessage(controller, CARD_NAME, err)` แล้ว return
 * - เงียบโดยตั้งใจ: `console.warn("[MeGen] …", err)` ใช้เฉพาะงานเสริมที่พังแล้วหน้าจอยังทำงานต่อได้ (search users, youtube status)
 * - อย่าลืมปิด loading/flag ทุก path ที่ return ก่อนเวลา
 * - `setErrorMessage` เก็บเป็น **string เสมอ** (แปลงด้วย `common/apiErrors.errorText`) ไม่เก็บ `ApiError` ทั้งก้อน
 *   เพราะ `ErrorMessage.tsx` จะ render object เป็นแถว status/message/data ดิบ ๆ
 * - "หลังบ้านปฏิเสธจริง" (4xx) กับ "อาการชั่วคราว" (network/5xx) ตัดสินด้วย `common/apiErrors.isClientError`
 *   — 4xx ห้ามให้หน้าจอ retry/fallback อัตโนมัติ (ยิงซ้ำก็ได้ผลเดิม)
 * - error ตอน poll state ใช้ `failStatePoll` (เงียบเมื่อชั่วคราว / 4xx โชว์ครั้งเดียวแล้วปิด flag ที่ค้ำ polling)
 *   ไม่งั้น snack เด้งซ้ำทุก 30 วิ
 */

import WasmController from "react-lib/frameworks/WasmController";
import axios, { AxiosResponse, AxiosError } from "axios";
import { AiUsageLoadError, AiUsageProgress, AiUsageRange, aiUsageRangeOf } from "./common/aiUsageLoading";
import { loadAllFfmpegUsageTasks, loadAllUsageSources } from "./common/ffmpegUsage";
import { loadAiUsageTitleIndex } from "./common/aiUsageTitles";
import { loadUsageProjectIndex, UsageProjectIndex, UsageProjectLoadError, UsageProjectProgress, UsageProjectRunMap } from "./common/aiUsageProjects";
import CONFIG from "config/config";

import {
  allStepsDone,
  anyStepError,
  anyStepRunning,
  firstStoppedStep,
  hasAnyStep,
  isStoppedByUser,
} from "./MeGenRunStatus";
import {
  RUNS_FILTER_INITIAL,
  RUNS_MAX_LIMIT,
  RUNS_SLICE_INITIAL,
  RUNS_PAGE_SIZE,
  RunsFilter,
  RunsSlice,
  RunsSliceKey,
  appendRuns,
  buildRunsParams,
  mergeRunIndex,
  refetchLimit,
  runsRequestKey,
  sliceFilter,
} from "./common/runsQuery";
import { ApiError, errorText, isClientError, isNotFound } from "./common/apiErrors";
import { emptySegmentVersions } from "./common/segmentVersions";
import {
  UserProfile,
  RunResponse,
  RunsResponse,
  RunData,
  ProjectPayload,
  ProjectSerializer,
  ViewType,
  ScheduleConfig,
  CARD_NAME,
  SeriesSerializer,
  CronConfig,
  StopRunSerializer,
  RunStateSerializer,
  SeriesType,
  UpdateItemPayload,
  UpdateMetadataPayload,
  UpdateItemSerializer,
  UpdateMetadataSerializer,
  RerunRunSerializer,
  DeleteRunSerializer,
  FinalScriptData,
  StartStoryPayload,
  SubmitPartsPayload,
  GenerateVideoFromScriptPayload,
  DirectGeneratePayload,
  Story2SceneGraphState,
  CreateRunPayload,
  StartRssNewsPayload,
  StartDirectScriptPayload,
  DirectScriptGraphState,
  ShareProjectPayload,
  ShareMemberSerializer,
  ShareProjectParams,
  DeleteSharedMemberPayload,
  SearchOaUserResponse,
  UpdateCompositionPayload,
  DirectScriptOutputResponse,
  AssetType,
  SegmentVersionResponse,
  SetCurrentVersionResponse,
  SetCurrentVersionPayload,
  YouTubeAuthResp,
  YouTubeStatusResp,
  StartScriptToScenePayload,
  StartScriptToSceneSerializer,
  ScriptToSceneGraphState,
  RefineScriptToScenePayload,
  RefineScriptToSceneResponse,
  StartGenerateDirectScriptPayload,
  CheckCostResponse,
  MediaStyle,
  UpdateTitlePayload,
  UpdateTitleResponse,
  CharacterSerializer,
  GenerateCharacterPayload,
  UploadCharacterPayload,
  ImageAsset,
  DeleteCharacterResponse,
  VoiceSerializer,
  ImageModelCatalog,
  VideoModelCatalog,
  DuplicateRunSerializer,
  AiUsageRecordRow,
  AiUsageRecordsResponse,
  FfmpegUsageResponse,
  AiUsageThreadDetail,
} from "./types/MeGenTypes";
import {
  AiUsageEvent,
  CharacterEvent,
  DirectScriptEvent,
  EpisodeEvent,
  LegacyEvent,
  MediaEvent,
  NavigateEvent,
  ProjectEvent,
  RssNewsEvent,
  ScriptToSceneEvent,
  SeriesEvent,
  ShareEvent,
  StorySceneEvent,
  YouTubeEvent,
} from "./MeGenEvents";
import { PickMainState, SetProperty } from "../HISV3/common/CommonInterface";

// APIS
import OfficialAccountUserList from "issara-sdk/apis/OfficialAccountUserList_apps_PRX";

// Serializers
import ProfiledDivisionHasUserSerializer from 'issara-sdk/types/ProfiledDivisionHasUserSerializer_apps_PRX'

// Consts
export const API_URL = CONFIG.PODCAST_API_URL as string;

/** limit ปลอมสำหรับ endpoint ที่ยังไม่มี pagination — ถ้าข้อมูลโตกว่านี้ต้องทำ pagination จริง */
const LIST_ALL_LIMIT = 9999;

/**
 * params หน้าแรกของแท็บ Videos ตอนเปิดโปรเจกต์
 * — ตัวกรองค่าเริ่มต้น ขอเท่าเพดาน server (ความลึกเท่าเดิมก่อนมี paging)
 */
const videosSeedParams = (projectId: number) =>
  buildRunsParams({ projectId, filter: RUNS_FILTER_INITIAL, offset: 0, limit: RUNS_MAX_LIMIT });

/**
 * ตั้งค่า slice จาก response ที่ดึงมาแล้ว (ไม่ยิง request เพิ่ม)
 * ใช้ตอนเปิดโปรเจกต์ — ตัวกรองกลับไปเป็นค่าเริ่มต้น ซึ่งตรงกับ params ที่เพิ่งใช้ดึง
 */
const seedRunsSlice = (resp: RunsResponse | null): RunsSlice => ({
  ...RUNS_FILTER_INITIAL,
  items: resp?.runs || [],
  total: resp?.total ?? 0,
  hasMore: resp?.has_more ?? false,
  loading: false,
  requestKey: "",
});

/** ต่อท้ายวันที่สุดท้ายของช่วง เพราะ backend filter แบบ `<= end_date` (ไม่งั้นจะตกทั้งวันสุดท้าย) */

/** ย้ายไป MeGenRunStatus.ts (คู่กับ isStoppedByUser / firstStoppedStep) — re-export ให้ import เดิมยังใช้ได้; ห้ามเทียบ === กับ values.error (ดู doc ที่นั่น) */
export { STOPPED_BY_USER } from "./MeGenRunStatus";

// HTTP status ที่ต้องแยกเคส
const HTTP_FORBIDDEN = 403;
const HTTP_NOT_FOUND = 404;
const HTTP_CONFLICT = 409;

// ชื่อ step ทั้งหมดในบล็อกนี้มาจาก backend

/** step ของเฟส script2scene = **pre-step ก่อนได้ draft** — ใช้เช็คว่า progress ยังไม่ข้ามออกจากเฟสนี้ */
const SCRIPT2SCENE_STEPS = [
  "ai_refine_script2scene",
  "script2scene_ai_script_splitter",
  "script2scene_save_result_to_json",
];
/** step ท้ายของฝั่ง draft — ถ้า key นี้โผล่ใน progress แล้วถือว่าพ้นช่วงเปลี่ยนเฟส (ใช้ตัดสิน isInTransition) */
const DRAFT_FINAL_STEPS = ["s2v_save_result_to_json"];
/** step ท้ายของฝั่งวิดีโอ — เดินมาถึงกลุ่มนี้ = workflow เข้าช่วงท้ายแล้ว */
const VDO_FINAL_STEPS = ["create_thumbnail", "upload_to_youtube"];

// --- API Response Types
type ApiErrorResponse = {
  message?: string;
  error?: string;
  detail?: string;
  [key: string]: any;
};

type ApiResponse<T> = [T, null] | [null, ApiError];

/*================================ SEQUENCE ==============================*/

export type State = {
  MeGenSequence: {
    // * --- default (เช่น config)
    defaultConfig: DefaultConfig;
    allOaUsersList: ProfiledDivisionHasUserSerializer[];
    youtubeStatus?: YouTubeStatusResp;

    // * --- screen
    currentView: ViewType;
    // * --- project section
    projectList: ProjectSerializer[];
    selectedProject?: ProjectSerializer;
    // * --- series section (related on selectedProject)
    seriesList: SeriesSerializer[];
    selectedSeries?: SeriesSerializer;
    // * --- generate section
    currentThreadId?: string;
    graphState?: RunStateSerializer;
    isGenerating?: boolean;
    isScriptToSceneGenerating?: boolean; // isGenenerating ของ script2scene (process ก่อน script2vdo)
    isRerun?: boolean;
    isJustStartedGenerate?: boolean;
    // * --- history (ตามหลักใช้ episode)
    /** จำนวน run ทั้งโปรเจกต์จาก server — badge แท็บ Videos (อัปเดตจากการดึง runsVideos ที่ไม่มีคำค้น) */
    runsTotal: number;
    /** ผลค้น/แบ่งหน้าของแท็บ Videos ที่ค้นมาจาก server (ทั้งโปรเจกต์) */
    runsVideos: RunsSlice;
    /**
     * ผลค้น/แบ่งหน้าของแผง episode ในแท็บ Playlist
     * — `seriesId` ใน slice = series ที่เปิดแผงอยู่ (ไม่มี = แผงปิด)
     */
    runsPlaylist: RunsSlice;
    /** แคช id→run สะสมจากทุกครั้งที่โหลด — ใช้หาชื่อ run ที่ไม่อยู่ในผลค้นหาปัจจุบัน */
    runIndex: Record<string, RunData>;

    // * --- project detail refresh state
    isProjectDetailRefreshing?: boolean;

    // * --- config styles
    mediaStyleList: MediaStyle[];
    voiceList: VoiceSerializer[];
    imageModelCatalog: ImageModelCatalog | null; // null = ยังไม่โหลด/โหลดไม่สำเร็จ
    videoModelCatalog: VideoModelCatalog | null; // null = ยังไม่โหลด/โหลดไม่สำเร็จ

    // * --- characters
    characterList: CharacterSerializer[];
    isCharacterGenerating?: boolean;

    // * --- ai usage (MeGenCost dashboard)
    aiUsageRecords: AiUsageRecordRow[];
    aiUsageRecordsTruncated?: boolean; // Incomplete request; never commit partial rows.
    aiUsageAppliedRange?: AiUsageRange;
    aiUsageProgress?: AiUsageProgress;
    aiUsageLoadError?: "cancelled" | "inconsistent" | "request";
    aiUsageTitleProgress?: { resolved: number; total: number; failed: number; loading: boolean };
    aiUsageLoading?: boolean;
    aiUsageThreadTitles: Record<string, string>; // thread_id -> ชื่อ video (จาก /ai-usage/thread-titles)
    aiUsageProjectIndex?: UsageProjectIndex;
    aiUsageProjectProgress?: UsageProjectProgress;
    aiUsageProjectError?: "cancelled" | "inconsistent" | "request";
  };
};

type DefaultConfig = Partial<{
  scheduleConfig: ScheduleConfig;
  cutoffHour: number; // 0 - 24 -> ตอนใส่ payload cutoff_days ให้แปลงเป๋็นทศนิยม เพราะ field นี้ใส่ได้ตั้งแต่ 0 - 1 (1 = 24 hours)
}>;

export const StateInitial: State = {
  MeGenSequence: {
    // * --- default (เช่น config)
    defaultConfig: {
      scheduleConfig: undefined,
      cutoffHour: 24,
    },
    allOaUsersList: [],
    youtubeStatus: undefined,

    // * --- screen
    currentView: "project-detail",
    // * --- project section
    projectList: [],
    selectedProject: undefined,
    // * --- series section (related on selectedProject)
    seriesList: [],
    selectedSeries: undefined,
    // * --- generate section
    currentThreadId: undefined,
    graphState: undefined,
    isGenerating: false,
    isScriptToSceneGenerating: false,
    isRerun: false,
    isJustStartedGenerate: false,

    // * --- history (ตามหลักใช้ episode)
    runsTotal: 0,
    runsVideos: { ...RUNS_SLICE_INITIAL },
    runsPlaylist: { ...RUNS_SLICE_INITIAL },
    runIndex: {},

    // * --- project detail refresh state
    isProjectDetailRefreshing: false,

    // * --- config styles
    mediaStyleList: [],
    voiceList: [],
    imageModelCatalog: null,
    videoModelCatalog: null,

    // * --- characters
    characterList: [],
    isCharacterGenerating: false,

    // * --- ai usage (MeGenCost dashboard)
    aiUsageRecords: [],
    aiUsageRecordsTruncated: false,
    aiUsageLoading: false,
    aiUsageThreadTitles: {},
  },
};

export type Event =
  | LegacyEvent
  | ProjectEvent
  | NavigateEvent
  | SeriesEvent
  | EpisodeEvent
  | RssNewsEvent // rss_news
  | DirectScriptEvent // script2vdo
  | StorySceneEvent // story2scene
  | ScriptToSceneEvent // script2scene
  | ShareEvent
  | YouTubeEvent
  | CharacterEvent
  | MediaEvent
  | AiUsageEvent
;

export type PickedState = PickMainState<
  "loadingStatus" | "buttonLoadCheck" | "errorMessage" | "successMessage" | "selectedDivision"
>;

export type Props = {};

export type Data = {
  division?: number;
};

export const DataInitial: Data = {};

export type SetProp = SetProperty<State & PickedState>;

export type Controller = WasmController<State & PickedState, Event, Data, Props>;

export type Handler = (controller: Controller, params?: any) => any;

/**========================================================================
 **                            COMMON HANDLERS
 *========================================================================**/

export const HandleStartMeGen: Handler = async (controller, params) => {
  const authToken = getAuthToken(controller);

  // -- init (allSettled: ถ้า schedule หรือ oa users พัง ต้องไม่ทำให้ bootstrap ล้มทั้งก้อน)
  const results = await Promise.allSettled([
    getSchedule(authToken),
    searchOaUsers(controller, { query: "" }),
  ]);

  const defaultSchduleConfig = results[0].status === 'fulfilled' ? results[0].value : null;
  const oaUsersResponse = results[1].status === 'fulfilled' ? results[1].value : [null, "Error"];
  const allOaUsersList: ProfiledDivisionHasUserSerializer[] = oaUsersResponse[0]?.items || [];

  if (params.restart) {
    controller.setState({
      MeGenSequence: {
        ...StateInitial.MeGenSequence,
        defaultConfig: {
          ...StateInitial.MeGenSequence?.defaultConfig,
          scheduleConfig: {
            ...StateInitial.MeGenSequence?.defaultConfig?.scheduleConfig,
            cron: defaultSchduleConfig as CronConfig,
          },
        },
        allOaUsersList,
      },
    });
  } else {
    controller.setState((prev) => ({
      MeGenSequence: {
        ...prev.MeGenSequence,
        defaultConfig: {
          ...prev.MeGenSequence?.defaultConfig,
          scheduleConfig: {
            ...prev.MeGenSequence?.defaultConfig?.scheduleConfig,
            cron: defaultSchduleConfig as CronConfig,
          },
        },
        allOaUsersList,
      },
    }));
  }

  HandleGetYouTubeStatus(controller, {});
  HandleGetProjectList(controller, params);

  // -- init styles
  HandleGetMediaStyles(controller, {});
  HandleGetVoiceList(controller, {});
  HandleGetImageModels(controller, {});
  HandleGetVideoModels(controller, {});
};

/**========================================================================
 **                            MEDIA HANDLERS
 *========================================================================**/

export const HandleGetMediaStyles: Handler = async (controller, params) => {
  const authToken = getAuthToken(controller);

  const [stylesResp, stylesErr] = await getMediaStyles(authToken);

  if (stylesErr || !stylesResp) {
    return;
  }

  controller.setState(prev => ({
    MeGenSequence: {
      ...prev.MeGenSequence,
      mediaStyleList: stylesResp,
    },
  }));
};

export const HandleGetVoiceList: Handler = async (controller, params) => {
  const authToken = getAuthToken(controller);
  const [voicesResp, voicesErr] = await getVoiceList(authToken);
  if (voicesErr || !voicesResp) return;
  controller.setState(prev => ({
    MeGenSequence: {
      ...prev.MeGenSequence,
      voiceList: voicesResp,
    },
  }));
};

// catalog โมเดล image (list/default/ความสามารถ) — ล้มเหลว/ว่าง = คง null ไว้ ให้ UI fallback เอง
export const HandleGetImageModels: Handler = async (controller, params) => {
  const authToken = getAuthToken(controller);
  const [catalogResp, catalogErr] = await getImageModels(authToken);
  if (catalogErr || !catalogResp?.models?.length) return;
  controller.setState(prev => ({
    MeGenSequence: {
      ...prev.MeGenSequence,
      imageModelCatalog: catalogResp,
    },
  }));
};

// catalog โมเดล video (list/default/alias/ความสามารถ) — ล้มเหลว/ว่าง = คง null ไว้ ให้ UI fallback เอง
export const HandleGetVideoModels: Handler = async (controller, params) => {
  const authToken = getAuthToken(controller);
  const [catalogResp, catalogErr] = await getVideoModels(authToken);
  if (catalogErr || !catalogResp?.models?.length) return;
  controller.setState(prev => ({
    MeGenSequence: {
      ...prev.MeGenSequence,
      videoModelCatalog: catalogResp,
    },
  }));
};

/**========================================================================
 **                            PROJECT HANDLERS
 *========================================================================**/

export const HandleGetProjectList: Handler = async (controller, params) => {
  const authToken = getAuthToken(controller);

  setLoadingStatus(controller, CARD_NAME, true);

  const [projListResp, projectListErr] = await getProjectList(authToken);

  if (projectListErr || !projListResp) {
    setLoadingStatus(controller, CARD_NAME, false);
    setErrorMessage(controller, CARD_NAME, projectListErr);

    return;
  }

  setLoadingStatus(controller, CARD_NAME, false);

  controller.setState((prev) => ({
    MeGenSequence: {
      ...prev.MeGenSequence,
      projectList: projListResp,
    },
  }));
};

export const HandleCreateProject: Handler = async (controller, params) => {
  const { data, onSuccess } = params;

  const authToken = getAuthToken(controller);

  setLoadingStatus(controller, CARD_NAME, true);

  const [createdResp, createdErr] = await createProject(authToken, data);

  if (createdErr || !createdResp) {
    setLoadingStatus(controller, CARD_NAME, false);
    setErrorMessage(controller, CARD_NAME, createdErr);

    return;
  }

  // -- fetch list
  const [projListResp] = await getProjectList(authToken);

  setLoadingStatus(controller, CARD_NAME, false);

  controller.setState((prev) => ({
    MeGenSequence: {
      ...prev.MeGenSequence,
      projectList: projListResp || [],
      selectedProject: createdResp,
    },
  }));

  // -- Go to Detail
  await HandleGetSeriesList(controller, { projectId: createdResp.id });

  if (onSuccess) {
    onSuccess();
  }
};

export const HandleEditProject: Handler = async (controller, params) => {
  const { projectId, data, onSuccess } = params;

  const authToken = getAuthToken(controller);

  setLoadingStatus(controller, CARD_NAME, true);

  const [updatedResp, updatedErr] = await updateProject(authToken, projectId, data);

  if (updatedErr || !updatedResp) {
    setLoadingStatus(controller, CARD_NAME, false);
    setErrorMessage(controller, CARD_NAME, updatedErr);

    return;
  }

  // -- fetch List
  const [projListResp] = await getProjectList(authToken);

  setLoadingStatus(controller, CARD_NAME, false);

  controller.setState((prev) => ({
    MeGenSequence: {
      ...prev.MeGenSequence,
      projectList: projListResp || [],
    },
  }));

  if (onSuccess) {
    onSuccess();
  }
};

export const HandleDeleteProject: Handler = async (controller, params) => {
  const { projectId, onSuccess } = params;

  const authToken = getAuthToken(controller);

  setLoadingStatus(controller, CARD_NAME, true);

  const [, deletedErr] = await deleteProject(authToken, projectId);

  if (deletedErr) {
    setLoadingStatus(controller, CARD_NAME, false);
    setErrorMessage(controller, CARD_NAME, deletedErr);

    return;
  }

  const currentProject = controller.getState().MeGenSequence?.selectedProject;

  // -- fetch List
  const [projListResp] = await getProjectList(authToken);

  setLoadingStatus(controller, CARD_NAME, false);
  setSuccessMessage(controller, CARD_NAME, `Delete Project Success!`)

  controller.setState((prev) => ({
    MeGenSequence: {
      ...prev.MeGenSequence,
      projectList: projListResp || [],
      ...(currentProject?.id === projectId && { selectedProject: undefined }),
    },
  }));

  if (onSuccess) {
    onSuccess();
  }
};

/**========================================================================
 **                      PLAYLIST (SERIES) HANDLERS
 *========================================================================**/

export const HandleGetSeriesList: Handler = async (controller, params) => {
  const { projectId } = params;
  const authToken = getAuthToken(controller);
  const state = controller.getState();

  const selectedProject = state.MeGenSequence.projectList.find(p => p.id === projectId);

  controller.setState(prev => ({
    MeGenSequence: {
      ...prev.MeGenSequence,
      selectedProject: selectedProject || prev.MeGenSequence.selectedProject,
      currentView: "project-detail",
      isProjectDetailRefreshing: true,
    },
  }));

  if (!projectId) {
    setErrorMessage(controller, CARD_NAME, "Project is not selected!");
    controller.setState(prev => ({
      MeGenSequence: { ...prev.MeGenSequence, isProjectDetailRefreshing: false },
    }));
    return;
  }

  const [
    [selectedProjResp],
    [seriesListResp],
    [runListResp],
  ] = await Promise.all([
    getProjectDetail(authToken, projectId),
    getSeriesList(authToken, projectId),
    getRuns(authToken, videosSeedParams(projectId)),
  ]);

  const currentState = controller.getState();

  if (currentState.MeGenSequence.selectedProject?.id !== projectId) {
    // Project changed — clear the flag but don't update stale data
    controller.setState(prev => ({
      MeGenSequence: { ...prev.MeGenSequence, isProjectDetailRefreshing: false },
    }));
    return;
  }

  controller.setState((prev) => ({
    MeGenSequence: {
      ...prev.MeGenSequence,
      // Do NOT override currentView — user may have navigated away during fetch
      selectedProject: selectedProjResp || undefined,
      seriesList: seriesListResp || [],
      runsTotal: runListResp?.total ?? 0,
      runsVideos: seedRunsSlice(runListResp),
      // เปิดโปรเจกต์ (ใหม่หรือกลับมา) = ปิดแผง episode ของ Playlist
      // — ตัวกรองแท็บ Videos ก็ถูกรีเซ็ตที่นี่เช่นกัน
      runsPlaylist: { ...RUNS_SLICE_INITIAL },
      runIndex: mergeRunIndex(prev.MeGenSequence?.runIndex || {}, runListResp?.runs || []),
      isProjectDetailRefreshing: false,
    },
  }));
};

/**
 * @deprecated ไม่มี UI เรียก (แผง Playlist เลือก series ผ่าน `runsPlaylist` แทน) — ยังต้อง export เพราะ
 * MainHISController.tsx:3175-3176 อ้างถึง; ลบ handler + case ใน host + type ใน MeGenEvents พร้อมกัน
 * ในรอบที่แตะ host repo
 */
export const HandleSelectSeries: Handler = async (controller, params) => {
  const { projectId, seriesId } = params;
  const seriesType: SeriesType = params.seriesType;
  const authToken = getAuthToken(controller);

  setLoadingStatus(controller, CARD_NAME, true);

  if (!seriesId) {
    setErrorMessage(controller, CARD_NAME, "Series is not selected!");

    return;
  }

  const [selectedSeriesResp] = await getSeriesDetail(authToken, projectId, seriesId);

  setLoadingStatus(controller, CARD_NAME, false);

  controller.setState(prev => ({
    MeGenSequence: {
      ...prev.MeGenSequence,
      currentView: seriesType === "rss_news"
        ? "create-from-website"
        : "create-from-script",
      selectedSeries: selectedSeriesResp || undefined,
      currentThreadId: undefined,
      graphState: undefined,
      isGenerating: false,
    },
  }));
};

export const HandleCreateSeries: Handler = async (controller, params) => {
  const data: SeriesSerializer = params.data;
  const authToken = getAuthToken(controller);

  setLoadingStatus(controller, CARD_NAME, true);

  const [createdResp, createdErr] = await createSeries(authToken, data);

  if (createdErr || !createdResp.id) {
    setLoadingStatus(controller, CARD_NAME, false);
    setErrorMessage(controller, CARD_NAME, createdErr);
    return;
  }

  // -- fetch list (series เท่านั้น — การสร้าง series ไม่กระทบ run ⇒ runsVideos/runsTotal/runIndex ยังถูกต้อง)
  const [seriesListResp] = await getSeriesList(authToken, data.project_id);

  setLoadingStatus(controller, CARD_NAME, false);
  setSuccessMessage(controller, CARD_NAME, `Create Series: ${data.name} Done!`);

  controller.setState((prev) => ({
    MeGenSequence: {
      ...prev.MeGenSequence,
      selectedSeries: createdResp || undefined,
      seriesList: seriesListResp || [],
    },
  }));

  if (params.onSuccess) {
    params.onSuccess();
  }
};

export const HandleEditSeries: Handler = async (controller, params) => {
  const { projectId, seriesId, onSuccess } = params;
  const data: SeriesSerializer = params.data;
  const authToken = getAuthToken(controller);

  setLoadingStatus(controller, CARD_NAME, true);

  const [updatedResp, updatedErr] = await updateSeries(authToken, projectId, seriesId, data);

  if (updatedErr || !updatedResp) {
    setLoadingStatus(controller, CARD_NAME, false);
    setErrorMessage(controller, CARD_NAME, updatedErr);

    return;
  }

  // -- fetch List
  const [seriesListResp] = await getSeriesList(authToken, projectId);

  setLoadingStatus(controller, CARD_NAME, false);
  setSuccessMessage(controller, CARD_NAME, `Edit Series: ${data.name} Done!`);

  controller.setState((prev) => ({
    MeGenSequence: {
      ...prev.MeGenSequence,
      seriesList: seriesListResp || [],
      selectedSeries: updatedResp,
    },
  }));

  if (onSuccess) {
    onSuccess();
  }
};

export const HandleDeleteSeries: Handler = async (controller, params) => {
  const { projectId, seriesId, confirm, onSuccess, onConflict } = params;
  const authToken = getAuthToken(controller);

  setLoadingStatus(controller, CARD_NAME, true);

  const [, deletedErr] = await deleteSeries(authToken, projectId, seriesId, confirm);

  if (deletedErr) {
    setLoadingStatus(controller, CARD_NAME, false);

    if (deletedErr.status === HTTP_CONFLICT && deletedErr.data?.detail?.requires_confirm) {
      onConflict?.(deletedErr.data.detail.message);
      return;
    }

    setErrorMessage(controller, CARD_NAME, deletedErr);
    return;
  }

  // -- fetch List
  const [seriesListResp] = await getSeriesList(authToken, projectId);

  setLoadingStatus(controller, CARD_NAME, false);
  setSuccessMessage(controller, CARD_NAME, `Delete Series Success!`);

  controller.setState((prev) => ({
    MeGenSequence: {
      ...prev.MeGenSequence,
      seriesList: seriesListResp || [],
      // series ที่เปิดแผง episode อยู่ถูกลบ → ปิดแผง (ไม่งั้นค้าง episode ของ series ที่ไม่มีแล้ว)
      ...(prev.MeGenSequence.runsPlaylist?.seriesId === seriesId ? { runsPlaylist: { ...RUNS_SLICE_INITIAL } } : {}),
    },
  }));

  if (onSuccess) {
    onSuccess();
  }
};

export const HandleNavigateToProjectPage: Handler = async (controller, params) => {
  const state = controller.getState();
  const currentProject = state.MeGenSequence?.selectedProject;

  if (!currentProject?.id) {
    console.warn("[NaviageToProjectPage] Project Not Found!");
    return;
  }

  controller.setState(prev => ({
    MeGenSequence: {
      ...prev.MeGenSequence,
      currentView: "project-detail",
      selectedSeries: undefined,
      // episode section
      currentThreadId: undefined,
      graphState: undefined,
      isGenerating: false,
    },
  }));

  HandleGetSeriesList(controller, { projectId: currentProject.id });
};

/**========================================================================
 **                       VIDEO (EPISODE) HANDLERS
 *========================================================================**/

export const HandleSelectEpisode: Handler = async (controller, params) => {
  const { episodeId } = params;
  const runType: SeriesType = params.runType;

  setLoadingStatus(controller, CARD_NAME, true);

  switch (runType) {
    case "rss_news":
      controller.setState(prev => ({
        MeGenSequence: {
          ...prev.MeGenSequence,
          currentThreadId: episodeId,
          currentView: "generate-video",
          isGenerating: true, // for start polling
        },
      }));

      setLoadingStatus(controller, CARD_NAME, false);

      break;
    case "story2scene":
      controller.setState(prev => ({
        MeGenSequence: {
          ...prev.MeGenSequence,
          currentThreadId: episodeId,
          currentView: "generate-script",
          isGenerating: true, // for start polling
        },
      }));

      setLoadingStatus(controller, CARD_NAME, false);

      break;

    case "script2vdo":
      controller.setState(prev => ({
        MeGenSequence: {
          ...prev.MeGenSequence,
          currentThreadId: episodeId,
          currentView: "generate-direct-script",
          isGenerating: true,
        },
      }));

      setLoadingStatus(controller, CARD_NAME, false);
      break;

    default:
      // Fallback to rss_news behavior
      setLoadingStatus(controller, CARD_NAME, false);
      setErrorMessage(controller, CARD_NAME, "Error to open episode");
      break;
  }
};

/**========================================================================
 **                            RSS HANDLERS
 *========================================================================**/

export const HandleCreateVideo: Handler = async (controller, params) => {
  const authToken = getAuthToken(controller);
  const state = controller.getState();
  const selectedProject = state.MeGenSequence?.selectedProject;

  const seriesId = params.seriesId !== undefined
    ? params.seriesId
    : state.MeGenSequence?.selectedSeries?.id || null;

  if (!selectedProject?.id) {
    setErrorMessage(controller, CARD_NAME, "No project selected");
    return;
  }

  const createPayload: CreateRunPayload = {
    project_id: selectedProject.id,
    series_id: seriesId,
    type: "rss_news",
  };

  const [runResp, runErr] = await createRun(authToken, createPayload);

  if (runErr || !runResp) {
    setErrorMessage(controller, CARD_NAME, runErr);
    return;
  }

  controller.setState(prev => ({
    MeGenSequence: {
      ...prev.MeGenSequence,
      currentView: "generate-video",
      currentThreadId: runResp.thread_id,
    },
  }));

  if (params.onSuccess) {
    params.onSuccess();
  }

  // after create done start run immediately
  HandleStartRun(controller, {
    threadId: runResp.thread_id,
    dryRun: params.data?.dry_run ?? true,
    manualScript: params.data?.manual_script ?? null,
  });
};

export const HandleStartRun: Handler = async (controller, params) => {
  const { threadId, dryRun = true, manualScript = null } = params;
  const authToken = getAuthToken(controller);

  controller.setState(prev => ({
    MeGenSequence: {
      ...prev.MeGenSequence,
      isGenerating: true,
    },
  }));

  const startPayload: StartRssNewsPayload = {
    dry_run: dryRun,
    manual_script: manualScript,
  };

  const [, startRunErr] = await startRun(controller, authToken, threadId, startPayload);

  if (startRunErr) {
    setErrorMessage(controller, CARD_NAME, startRunErr);

    controller.setState(prev => ({
      MeGenSequence: {
        ...prev.MeGenSequence,
        isGenerating: false,
      },
    }));

    return;
  }
};

export const HandleStopRun: Handler = async (controller, params) => {
  const { threadId, onSuccess } = params;
  const authToken = getAuthToken(controller);

  const [stopRunResp, stopRunErr] = await stopRun(authToken, threadId);

  if (stopRunErr || !stopRunResp) {
    setErrorMessage(controller, CARD_NAME, stopRunErr);

    // -- stop generate / run when error
    controller.setState(prev => ({
      MeGenSequence: {
        ...prev.MeGenSequence,
        currentThreadId: threadId,
        isGenerating: false,
      },
    }));

    return;
  }

  controller.setState(prev => ({
    MeGenSequence: {
      ...prev.MeGenSequence,
      currentThreadId: stopRunResp.thread_id,
      isGenerating: false,
    },
  }));

  onSuccess?.();
};

export const HandleUploadYouTube: Handler = async (controller, params) => {
  const { threadId, credentialId, onSuccess } = params;
  const authToken = getAuthToken(controller);

  const [, resumeRunErr] = await resumeRun(authToken, threadId, credentialId ?? null);

  if (resumeRunErr) {
    setErrorMessage(controller, CARD_NAME, resumeRunErr);

    controller.setState(prev => ({
      MeGenSequence: {
        ...prev.MeGenSequence,
        isGenerating: false,
      },
    }));

    return;
  }

  if (onSuccess) {
    onSuccess();
  }
};

export const HandleUploadYouTubeDirectScript: Handler = async (controller, params) => {
  const { threadId, credentialId, onSuccess } = params;
  const authToken = getAuthToken(controller);

  controller.setState(prev => ({
    MeGenSequence: {
      ...prev.MeGenSequence,
      isGenerating: true,
    },
  }));

  const [, resumeRunErr] = await resumeRun(authToken, threadId, credentialId ?? null);

  if (resumeRunErr) {
    setErrorMessage(controller, CARD_NAME, resumeRunErr);
    controller.setState(prev => ({
      MeGenSequence: { ...prev.MeGenSequence, isGenerating: false },
    }));
    return;
  }

  // ปล่อย isGenerating เป็น true ต่อโดยตั้งใจ เพื่อให้ useRunPolling ตาม step upload ต่อจนจบ
  // (polling รอบถัดไปจะปิดเอง) — โค้ดเดิม set isUploading: false ที่ไม่มีใครอ่าน จึงลบออก

  if (onSuccess) {
    const [stateResp] = await getDirectScriptState(authToken, threadId);
    onSuccess(stateResp, true);
  }
};

export const HandleUpdateVideoItem: Handler = async (controller, params) => {
  const { threadId, onSuccess } = params;
  const data: UpdateItemPayload = params.data;
  const authToken = getAuthToken(controller);

  // ไม่เรียก setLoadingStatus ที่นี่ — หน้าจอโชว์สถานะจาก isJustStartedGenerate/isGenerating แทน
  controller.setState(prev => ({
    MeGenSequence: {
      ...prev.MeGenSequence,
      isJustStartedGenerate: true,
    },
  }));

  const [updatedResp, updatedErr] = await updateRunItem(authToken, threadId, data);

  if (updatedErr || !updatedResp) {
    setErrorMessage(controller, CARD_NAME, updatedErr);

    return;
  }

  // -- fetch run state
  const [runResp, runErr] = await getRunState(authToken, threadId);

  if (runErr || !runResp) {
    setErrorMessage(controller, CARD_NAME, runErr);

    return;
  }


  if (onSuccess) {
    onSuccess(runResp, true);
  }

  controller.setState(prev => ({
    MeGenSequence: {
      ...prev.MeGenSequence,
      currentView: "generate-video",
      graphState: runResp,
      currentThreadId: runResp?.config?.configurable?.thread_id,
      isGenerating: false,
    },
  }));
};

export const HandleUpdateMetadata: Handler = async (controller, params) => {
  const { threadId, onSuccess } = params;
  const data: UpdateMetadataPayload = params.data;
  const authToken = getAuthToken(controller);

  // ไม่เรียก setLoadingStatus ที่นี่ — หน้าจอโชว์สถานะจาก isJustStartedGenerate/isGenerating แทน
  controller.setState(prev => ({
    MeGenSequence: {
      ...prev.MeGenSequence,
      isJustStartedGenerate: true,
    },
  }));


  const [updatedResp, updatedErr] = await updateRunMetadata(authToken, threadId, data);

  if (updatedErr || !updatedResp) {
    setErrorMessage(controller, CARD_NAME, updatedErr);

    return;
  }

  // -- fetch run state
  const [runResp, runErr] = await getRunState(authToken, threadId);

  if (runErr || !runResp) {
    setErrorMessage(controller, CARD_NAME, runErr);

    return;
  }


  if (onSuccess) {
    onSuccess(runResp, true);
  }

  controller.setState(prev => ({
    MeGenSequence: {
      ...prev.MeGenSequence,
      currentView: "generate-video",
      graphState: runResp,
      currentThreadId: runResp?.config?.configurable?.thread_id,
      isGenerating: false,
    },
  }));
};

export const HandleUpdateComposition: Handler = async (controller, params) => {
  const { threadId, data, onSuccess } = params;
  const authToken = getAuthToken(controller);

  const [, err] = await updateComposition(authToken, threadId, data);

  if (err) {
    setErrorMessage(controller, CARD_NAME, err);
    return;
  }

  // -- fetch run state
  const [runResp, runErr] = await getRunState(authToken, threadId);

  if (runErr || !runResp) {
    setErrorMessage(controller, CARD_NAME, runErr);
    return;
  }

  if (onSuccess) {
    onSuccess(runResp, true);
  }

  controller.setState(prev => ({
    MeGenSequence: {
      ...prev.MeGenSequence,
      currentView: "generate-video",
      graphState: runResp,
      currentThreadId: runResp?.config?.configurable?.thread_id,
      isGenerating: false,
    },
  }));
};

export const HandleRerunRun: Handler = async (controller, params) => {
  const { threadId } = params;
  const authToken = getAuthToken(controller);

  const [rerunResp, rerunErr] = await rerunRun(authToken, threadId);

  if (rerunErr || !rerunResp) {
    setErrorMessage(controller, CARD_NAME, rerunErr);
    return;
  }

  controller.setState(prev => ({
    MeGenSequence: {
      ...prev.MeGenSequence,
      currentView: "generate-video",
      currentThreadId: rerunResp.thread_id,
      isGenerating: true,
      isRerun: true,
    },
  }));
};

/**
 * โหลดซ้ำทุก slice ที่มีผลแสดงอยู่ หลัง run ถูกลบ/duplicate — คงตัวกรองและความลึกที่ผู้ใช้กด "โหลดเพิ่ม" ไว้
 * Playlist โหลดเฉพาะเมื่อมี series เปิดแผงอยู่ (slice ว่าง = ไม่มีอะไรให้ refresh)
 * (อ่าน filter จาก state ตรงนี้ปลอดภัย — เป็นค่าที่เขียนไว้ตั้งแต่ tick ก่อน ไม่ใช่ค่าที่เพิ่ง setState)
 */
const refreshRunSlices = async (controller: Controller, projectId: number) => {
  const seq = controller.getState().MeGenSequence;
  const videos = seq?.runsVideos || RUNS_SLICE_INITIAL;
  const playlist = seq?.runsPlaylist || RUNS_SLICE_INITIAL;

  await Promise.all([
    HandleSearchRuns(controller, { projectId, filter: sliceFilter(videos), mode: "refresh", slice: "runsVideos" }),
    ...(playlist.seriesId != null
      ? [
          HandleSearchRuns(controller, {
            projectId,
            filter: sliceFilter(playlist),
            mode: "refresh",
            slice: "runsPlaylist",
          }),
        ]
      : []),
  ]);
};

export const HandleDuplicateRun: Handler = async (controller, params) => {
  const { threadId } = params;
  const authToken = getAuthToken(controller);
  const state = controller.getState();

  setLoadingStatus(controller, CARD_NAME, true);

  const [duplicateResp, duplicateErr] = await duplicateRun(authToken, threadId);

  if (duplicateErr || !duplicateResp) {
    setErrorMessage(controller, CARD_NAME, duplicateErr);
    setLoadingStatus(controller, CARD_NAME, false);
    return;
  }

  const projectId = state.MeGenSequence?.selectedProject?.id || 0;

  setLoadingStatus(controller, CARD_NAME, false);

  controller.setState(prev => ({
    MeGenSequence: {
      ...prev.MeGenSequence,
      currentThreadId: undefined,
      graphState: undefined,
      isGenerating: false,
    },
  }));

  // โหลดลิสต์ที่แสดงอยู่ซ้ำ — ทั้งแท็บ Videos และแผง episode ของ Playlist (ถ้าเปิดอยู่)
  await refreshRunSlices(controller, projectId);

  if (params.onSuccess) {
    params.onSuccess();
  }
};

/**
 * ค้น / เรียง / โหลดเพิ่ม run โดยให้ **server** เป็นคนค้นจาก run ทั้งหมด — ใช้ร่วมกัน 2 slice:
 * `runsVideos` (แท็บ Videos ทั้งโปรเจกต์) และ `runsPlaylist` (แผง episode ของ series ที่เลือกในแท็บ Playlist)
 *
 * ⚠ **`filter` ต้องส่งมาทาง `params` เสมอ ห้ามอ่านกลับจาก state**
 * controller ตัวนี้ผูกกับ `setState` ของ class component React 16 (`MainHIS.tsx:351-357`)
 * ซึ่งถูก batch เมื่อ handler ถูกเรียกจาก onClick ⇒ อ่านค่าที่เพิ่งเขียนใน tick เดียวกันจะได้ค่าเก่า
 * ⇒ ปุ่ม sort / โหลดเพิ่ม / เลือก series จะยิงด้วยตัวกรองเก่า "ช้าไป 1 คลิก" แบบเงียบ ๆ ไม่มี error
 * (การอ่าน `items.length` / `seriesId` เดิมด้านล่างปลอดภัย เพราะเป็นค่าที่เขียนไว้ตั้งแต่ tick ก่อน)
 *
 * mode: `replace` = คำค้น/ลำดับ/series เปลี่ยน (เริ่มหน้าแรก) · `append` = กดโหลดเพิ่ม
 *       `refresh` = โหลดซ้ำหลังลบ/duplicate โดยคงความลึกที่ผู้ใช้กดสะสมไว้
 * Playlist: `filter.seriesId == null` = ปิดแผง → ล้าง slice โดยไม่ยิง request (slice นี้ผูกกับ series เสมอ)
 */
export const HandleSearchRuns: Handler = async (controller, params) => {
  const projectId: number = params.projectId;
  const filter: RunsFilter = params.filter;
  const mode: "replace" | "append" | "refresh" = params.mode || "replace";
  const slice: RunsSliceKey = params.slice || "runsVideos";
  const authToken = getAuthToken(controller);

  if (!projectId) return;

  const readSlice = (): RunsSlice => controller.getState().MeGenSequence?.[slice] || RUNS_SLICE_INITIAL;

  const patchSlice = (next: Partial<RunsSlice>) =>
    controller.setState((prevState) => ({
      MeGenSequence: {
        ...prevState.MeGenSequence,
        [slice]: { ...(prevState.MeGenSequence?.[slice] || RUNS_SLICE_INITIAL), ...next },
      },
    }));

  // ปิดแผง Playlist: เขียน slice ว่างทั้งก้อน
  // (requestKey กลับเป็น "" ⇒ response ที่ยังบินอยู่ถูก guard ด้านล่างทิ้งเอง)
  if (slice === "runsPlaylist" && filter.seriesId == null) {
    controller.setState((prevState) => ({
      MeGenSequence: { ...prevState.MeGenSequence, runsPlaylist: { ...RUNS_SLICE_INITIAL } },
    }));
    return;
  }

  const prev = readSlice();
  const loaded = prev.items.length;
  // เปลี่ยน series = ผลชุดเดิมเป็นของ series อื่น ห้ามค้างโชว์ใต้หัวข้อใหม่ระหว่างรอ
  // (Videos ไม่มี seriesId ⇒ ไม่เข้าเคสนี้)
  const seriesChanged = (prev.seriesId ?? null) !== (filter.seriesId ?? null);

  const request = {
    projectId,
    filter,
    offset: mode === "append" ? loaded : 0,
    limit: mode === "refresh" ? refetchLimit(loaded) : RUNS_PAGE_SIZE,
  };
  const requestKey = runsRequestKey(request);

  // เก็บ filter ลง state ทันที ช่องค้น/ปุ่ม sort/series ที่เลือก จะได้สะท้อนสิ่งที่ผู้ใช้เพิ่งทำ แม้ผลยังไม่กลับมา
  patchSlice({
    ...filter,
    loading: true,
    requestKey,
    ...(seriesChanged ? { items: [], total: 0, hasMore: false } : {}),
  });

  const [resp, err] = await getRuns(authToken, buildRunsParams(request));

  // มีคำขอใหม่แซงไปแล้ว → ทิ้ง response นี้เงียบ ๆ (ห้ามแตะ loading ของคำขอที่กำลังบินอยู่)
  if (readSlice().requestKey !== requestKey) return;

  if (err || !resp) {
    setErrorMessage(controller, CARD_NAME, err);
    patchSlice({ loading: false });
    return;
  }

  // ผลของแท็บ Videos ที่ไม่มีคำค้น = run ทั้งโปรเจกต์ ⇒ total คือค่า badge "Videos N"
  const isProjectWide = slice === "runsVideos" && !filter.q.trim();

  controller.setState((prevState) => {
    const current = prevState.MeGenSequence?.[slice] || RUNS_SLICE_INITIAL;

    return {
      MeGenSequence: {
        ...prevState.MeGenSequence,
        [slice]: {
          ...filter,
          items: mode === "append" ? appendRuns(current.items, resp.runs) : resp.runs,
          total: resp.total,
          hasMore: resp.has_more,
          loading: false,
          requestKey,
        },
        ...(isProjectWide ? { runsTotal: resp.total } : {}),
        runIndex: mergeRunIndex(prevState.MeGenSequence?.runIndex || {}, resp.runs),
      },
    };
  });
};

export const HandleDeleteRun: Handler = async (controller, params) => {
  const { threadId } = params;
  const authToken = getAuthToken(controller);
  const state = controller.getState();

  setLoadingStatus(controller, CARD_NAME, true);

  const [deleteResp, deleteErr] = await deleteRun(authToken, threadId);

  if (deleteErr || !deleteResp) {
    setErrorMessage(controller, CARD_NAME, deleteErr);
    setLoadingStatus(controller, CARD_NAME, false);
    return;
  }

  const projectId = state.MeGenSequence?.selectedProject?.id || 0;

  setLoadingStatus(controller, CARD_NAME, false);

  controller.setState(prev => ({
    MeGenSequence: {
      ...prev.MeGenSequence,
      currentThreadId: undefined,
      graphState: undefined,
      isGenerating: false,
    },
  }));

  // โหลดลิสต์ที่แสดงอยู่ซ้ำ — ทั้งแท็บ Videos และแผง episode ของ Playlist (ถ้าเปิดอยู่)
  await refreshRunSlices(controller, projectId);

  if (params.onSuccess) {
    params.onSuccess();
  }
};

export const HandleGetRunState: Handler = async (controller, params) => {
  const { threadId, onSuccess } = params;
  const authToken = getAuthToken(controller);

  const [stateResp, stateErr]  = await getRunState(authToken, threadId);

  if (stateErr || !stateResp) {
    failStatePoll(controller, stateErr, { isGenerating: false, isJustStartedGenerate: false });

    return;
  }

  const state = controller.getState();
  const isRerun = state.MeGenSequence?.isRerun;
  const wasJustStarted = state.MeGenSequence?.isJustStartedGenerate === true;

  const isAnyStepRunning = anyStepRunning(stateResp.progress);
  const hasReachedFinalStep = hasAnyStep(stateResp.progress, VDO_FINAL_STEPS);

  const isInTransition = stateResp.next.length === 0
    && !isAnyStepRunning
    && !hasReachedFinalStep;

  const isCompleted = stateResp.next.length === 0
    && stateResp.values
    && Object.keys(stateResp.values).length > 0
    && !isAnyStepRunning
    && !isInTransition;

  const isWaitingUpload = stateResp.next?.includes("upload_to_youtube");

  const hasError = anyStepError(stateResp.progress);

  // ผู้ใช้กดหยุด: ดูทั้ง graph state (มาหลังกราฟรับรู้ cancel) และ progress ของ step (POST /stop เขียนทันที)
  const isStopped = isStoppedByUser(stateResp.values?.error) || firstStoppedStep(stateResp.progress) !== null;
  const isWorkflowDone = isCompleted || isWaitingUpload || hasError || isStopped;

  let isGenerating = state.MeGenSequence?.isGenerating || false;

  if (isRerun) {
    if (isWorkflowDone) {
      if (wasJustStarted && !hasError) {
        isGenerating = true;
      } else {
        isGenerating = false;
      }
    } else {
      isGenerating = true;
    }
  } else {
    if (isWorkflowDone) {
      if (wasJustStarted && !hasError) {
        isGenerating = true;
      } else {
        isGenerating = false;
      }
    } else if (isAnyStepRunning || isInTransition) {
      isGenerating = true;
    }
  }

  const clearJustStarted = wasJustStarted ? { isJustStartedGenerate: false } : {};

  if (onSuccess) {
    onSuccess(stateResp, isGenerating);

    // update parent state
    controller.setState({
      MeGenSequence: {
        ...state.MeGenSequence,
        isGenerating,
        ...(isRerun && isWorkflowDone ? { isRerun: false } : {}),
        ...clearJustStarted,
      },
    });

    return;
  }

  controller.setState({
    MeGenSequence: {
      ...state.MeGenSequence,
      graphState: stateResp,
      isGenerating,
      ...(isRerun && isWorkflowDone ? { isRerun: false } : {}),
      ...clearJustStarted,
    },
  });
};

/**========================================================================
 **                          STORY2SCENE HANDLERS
 *========================================================================**/

export const HandleCreateStory2SceneRun: Handler = async (controller, params) => {
  const { projectId, seriesId, seriesType, onSuccess } = params;
  const authToken = getAuthToken(controller);

  const createPayload: CreateRunPayload = {
    project_id: projectId,
    series_id: seriesId,
    type: seriesType || "story2scene",
  };

  const [runResp, runErr] = await createRun(authToken, createPayload);

  if (runErr || !runResp) {
    setErrorMessage(controller, CARD_NAME, runErr);
    return;
  }

  controller.setState(prev => ({
    MeGenSequence: {
      ...prev.MeGenSequence,
      currentThreadId: runResp.thread_id,
    },
  }));

  if (onSuccess) {
    onSuccess(runResp.thread_id);
  }
};

export const HandleSubmitStory: Handler = async (controller, params) => {
  const { threadId, story } = params;
  const authToken = getAuthToken(controller);

  controller.setState(prev => ({
    MeGenSequence: {
      ...prev.MeGenSequence,
      isGenerating: true,
    },
  }));

  const [, err] = await startStory(authToken, threadId, {
    story,
    force_regen: true,
  });

  if (err) {
    setErrorMessage(controller, CARD_NAME, err);

    controller.setState(prev => ({
      MeGenSequence: {
        ...prev.MeGenSequence,
        isGenerating: false,
      },
    }));
  }
};

export const HandleSubmitParts: Handler = async (controller, params) => {
  const { threadId, parts } = params;
  const authToken = getAuthToken(controller);

  controller.setState(prev => ({
    MeGenSequence: {
      ...prev.MeGenSequence,
      isGenerating: true,
    },
  }));

  const [, err] = await submitParts(authToken, threadId, {
    full_parts: parts,
    force_regen: true,
  });

  if (err) {
    setErrorMessage(controller, CARD_NAME, err);

    controller.setState(prev => ({
      MeGenSequence: {
        ...prev.MeGenSequence,
        isGenerating: false,
      },
    }));
  }
};

export const HandleGenerateVideoFromScript: Handler = async (controller, params) => {
  const { threadId, script, dryRun } = params;
  const authToken = getAuthToken(controller);

  controller.setState(prev => ({
    MeGenSequence: {
      ...prev.MeGenSequence,
      isGenerating: true,
    },
  }));

  const [, err] = await generateVideoFromScript(authToken, threadId, { dry_run: dryRun, manual_script: script });

  if (err) {
    setErrorMessage(controller, CARD_NAME, err);

    controller.setState(prev => ({
      MeGenSequence: {
        ...prev.MeGenSequence,
        isGenerating: false,
      },
    }));
  }
};

export const HandleDirectGenerate: Handler = async (controller, params) => {
  const { threadId, projectId, seriesId, data, dryRun } = params;
  const authToken = getAuthToken(controller);

  let currentThreadId = threadId;

  if (!currentThreadId) {
    const createPayload: CreateRunPayload = {
      project_id: projectId,
      series_id: seriesId,
      type: "script2vdo",
    };

    const [runResp, runErr] = await createRun(authToken, createPayload);

    if (runErr || !runResp) {
      setErrorMessage(controller, CARD_NAME, runErr);
      return;
    }

    currentThreadId = runResp.thread_id;
  }

  controller.setState(prev => ({
    MeGenSequence: {
      ...prev.MeGenSequence,
      currentThreadId,
      isGenerating: true,
    },
  }));

  const [, err] = await directGenerate(authToken, currentThreadId, { dry_run: dryRun, manual_script: null, script2vdo: data });

  if (err) {
    setErrorMessage(controller, CARD_NAME, err);

    controller.setState(prev => ({
      MeGenSequence: {
        ...prev.MeGenSequence,
        isGenerating: false,
      },
    }));
  }
};

export const HandleGetStory2SceneState: Handler = async (controller, params) => {
  const { threadId, onSuccess } = params;
  const authToken = getAuthToken(controller);

  const [[stateResp, stateErr], [runStateResp]] = await Promise.all([
    getStory2SceneState(authToken, threadId),
    getRunState(authToken, threadId),
  ]);

  if (stateErr || !stateResp) {
    failStatePoll(controller, stateErr, { isGenerating: false, isJustStartedGenerate: false });
    return;
  }


  // ── Story state มี VDO progress แต่ไม่มี items/final_video → ต้อง merge จาก run state ──
  const storyHasItems = (stateResp.values?.items?.length || 0) > 0;
  const storyHasFinalVideo = !!stateResp.values?.final_video_path;
  const storyHasVdoProgress = hasAnyStep(stateResp.progress, VDO_FINAL_STEPS);

  // Merge เมื่อ: มี VDO progress ใน story state แต่ข้อมูล items/video ยังไม่ครบ
  const shouldMerge = storyHasVdoProgress && (!storyHasItems || !storyHasFinalVideo);

  let mergedState = stateResp as any;

  if (shouldMerge && runStateResp) {
    mergedState = {
      ...stateResp,
      next: runStateResp.next ?? stateResp.next, // ← ใช้ next จาก run (มี upload_to_youtube)
      values: {
        ...stateResp.values,
        ...runStateResp.values, // override: items, final_video_path, youtube_title ฯลฯ
      },
    };
  }

  // ── คำนวณ isWorkflowDone ──
  const mergedIsAnyStepRunning = anyStepRunning(mergedState.progress);
  const mergedHasAnyVdoStep = hasAnyStep(mergedState.progress, VDO_FINAL_STEPS);

  const isWaitingUpload = mergedState.next?.includes("upload_to_youtube");
  const hasError = anyStepError(mergedState.progress);

  const hasFinalScript = (mergedState.values?.final_script?.items?.length || 0) > 0;
  const isStoryPhaseDone = hasFinalScript && !mergedIsAnyStepRunning && !mergedHasAnyVdoStep;
  const isVdoCompleted = mergedHasAnyVdoStep
    && (mergedState.next?.length || 0) === 0
    && !mergedIsAnyStepRunning;

  const isWorkflowDone = isStoryPhaseDone || isVdoCompleted || isWaitingUpload || hasError;
  const isGenerating = !isWorkflowDone;

  if (onSuccess) {
    onSuccess(mergedState, isGenerating);
    controller.setState(prev => ({
      MeGenSequence: { ...prev.MeGenSequence, graphState: mergedState, isGenerating },
    }));
    return;
  }

  controller.setState(prev => ({
    MeGenSequence: { ...prev.MeGenSequence, graphState: mergedState, isGenerating },
  }));
};

/**========================================================================
 **                  SCRIPT2VDO (DIRECT SCRIPT) HANDLERS
 *========================================================================**/

export const HandleDirectScriptGenerate: Handler = async (controller, params) => {
  const { threadId, projectId, seriesId, data, dryRun, onSuccess } = params;
  const authToken = getAuthToken(controller);

  let currentThreadId = threadId;

  // Create run if not exists
  if (!currentThreadId) {
    const createPayload: CreateRunPayload = {
      project_id: projectId,
      series_id: seriesId,
      type: "script2vdo",
    };

    const [runResp, runErr] = await createRun(authToken, createPayload);

    if (runErr || !runResp) {
      setErrorMessage(controller, CARD_NAME, runErr);
      return;
    }

    currentThreadId = runResp.thread_id;
  }

  controller.setState(prev => ({
    MeGenSequence: {
      ...prev.MeGenSequence,
      currentView: "generate-direct-script",
      currentThreadId,
      isGenerating: true,
    },
  }));

  // Start direct script generation
  const [, err] = await startDirectScript(authToken, currentThreadId, {
    script2vdo: data,
    force: dryRun,
  });

  if (err) {
    setErrorMessage(controller, CARD_NAME, err);

    controller.setState(prev => ({
      MeGenSequence: {
        ...prev.MeGenSequence,
        isGenerating: false,
      },
    }));

    return;
  }

  if (onSuccess) {
    onSuccess(currentThreadId);
  }
};

export const HandleStartGenerateDirectScript: Handler = async (controller, params) => {
  const { threadId, data, onSuccess } = params;
  const authToken = getAuthToken(controller);

  controller.setState(prev => ({
    MeGenSequence: {
      ...prev.MeGenSequence,
      isGenerating: true,
      graphState: undefined,
      isJustStartedGenerate: true,
    },
  }));

  const [, startRunErr] = await startRun(controller, authToken, threadId, data);

  if (startRunErr) {
    setErrorMessage(controller, CARD_NAME, startRunErr);

    controller.setState(prev => ({
      MeGenSequence: {
        ...prev.MeGenSequence,
        isGenerating: false,
        // ปิด flag ที่เปิดไว้ตอนเริ่ม ไม่งั้น poll ถัดไปเห็น wasJustStarted แล้วบังคับ isGenerating กลับเป็น true
        // (จอกระพริบ "กำลังสร้าง" ทั้งที่ start ล้ม)
        isJustStartedGenerate: false,
      },
    }));

    return;
  }

  controller.setState(prev => ({
    MeGenSequence: {
      ...prev.MeGenSequence,
      currentView: "generate-direct-script",
      currentThreadId: threadId,
      isGenerating: true,
    },
  }));

  if (onSuccess) {
    onSuccess();
  }
};

// *** Run เพื่อเอา VDO HD
export const HandleStartGenerateDirectScriptFinal: Handler = async (controller, params) => {
  const { threadId, data, onSuccess } = params;
  const authToken = getAuthToken(controller);

  controller.setState(prev => ({
    MeGenSequence: {
      ...prev.MeGenSequence,
      isGenerating: true,
      graphState: undefined,
      isJustStartedGenerate: true,
    },
  }));

  const [, startRunErr] = await startFinalRun(authToken, threadId, data);

  if (startRunErr) {
    setErrorMessage(controller, CARD_NAME, startRunErr);

    controller.setState(prev => ({
      MeGenSequence: {
        ...prev.MeGenSequence,
        isGenerating: false,
        // ปิด flag ที่เปิดไว้ตอนเริ่ม ไม่งั้น poll ถัดไปเห็น wasJustStarted แล้วบังคับ isGenerating กลับเป็น true
        // (จอกระพริบ "กำลังสร้าง" ทั้งที่ start ล้ม)
        isJustStartedGenerate: false,
      },
    }));

    return;
  }

  controller.setState(prev => ({
    MeGenSequence: {
      ...prev.MeGenSequence,
      currentView: "generate-direct-script",
      currentThreadId: threadId,
      isGenerating: true,
    },
  }));

  if (onSuccess) {
    onSuccess();
  }
};

export const HandleUpdateDirectScriptTitle: Handler = async (controller, params) => {
  const { threadId, data, onSuccess, onError } = params;
  const authToken = getAuthToken(controller);

  const [result, error] = await updateDirectScriptTitle(authToken, threadId, data);

  if (error) {
    onError?.();
    return;
  }

  onSuccess?.(result!.title);
};

export const HandleUpdateDirectScript: Handler = async (controller, params) => {
  const { threadId, data } = params;
  const authToken = getAuthToken(controller);

  const [, err] = await updateDirectScript(authToken, threadId, data);

  if (err) {
    setErrorMessage(controller, CARD_NAME, err);
  }
};

export const HandleUploadDirectVideo: Handler = async (controller, params) => {
  const { threadId, itemId, file, onSuccess, onError } = params;
  const authToken = getAuthToken(controller);

  const formData = new FormData();
  formData.append("file", file);

  const [resp, err] = await uploadDirectVideo(authToken, threadId, itemId, formData);

  if (err || !resp) {
    setErrorMessage(controller, CARD_NAME, err);
    onError?.();

    return;
  }

  // -- update state
  const [stateResp, stateErr] = await getDirectScriptState(authToken, threadId);

  if (stateErr || !stateResp) {
    setErrorMessage(controller, CARD_NAME, stateErr);

    return;
  }

  setSuccessMessage(controller, CARD_NAME, "Video uploaded success!");

  controller.setState(prev => ({
    MeGenSequence: {
      ...prev.MeGenSequence,
      graphState: stateResp as any,
    },
  }));

  if (onSuccess) {
    onSuccess(resp.url);
  }
};

export const HandleUploadDirectImage: Handler = async (controller, params) => {
  const { threadId, itemId, file, onSuccess, onError } = params;
  const authToken = getAuthToken(controller);

  const formData = new FormData();
  formData.append("file", file);

  const [resp, err] = await uploadDirectImage(authToken, threadId, itemId, formData);

  if (err || !resp) {
    setErrorMessage(controller, CARD_NAME, err);
    onError?.();

    return;
  }

  // -- update state
  const [stateResp, stateErr] = await getDirectScriptState(authToken, threadId);

  if (stateErr || !stateResp) {
    setErrorMessage(controller, CARD_NAME, stateErr);

    return;
  }

  setSuccessMessage(controller, CARD_NAME, "Image uploaded success!");

  controller.setState(prev => ({
    MeGenSequence: {
      ...prev.MeGenSequence,
      graphState: stateResp as any,
    },
  }));

  if (onSuccess) {
    onSuccess(resp.url);
  }
};

export const HandleUploadImageOverlay: Handler = async (controller, params) => {
  const { threadId, itemId, file, onSuccess, onError } = params;
  const authToken = getAuthToken(controller);

  const formData = new FormData();
  formData.append("file", file);

  const [resp, err] = await uploadImageOverlay(authToken, threadId, itemId, formData);

  if (err || !resp) {
    setErrorMessage(controller, CARD_NAME, err);
    onError?.();
    return;
  }

  if (onSuccess) {
    onSuccess(resp.url);
  }
};

export const HandleUploadImageAsset: Handler = async (controller, params) => {
  const { threadId, file, onSuccess, onError } = params;
  const authToken = getAuthToken(controller);

  const formData = new FormData();
  formData.append("file", file);

  const [, uploadErr] = await uploadImageAsset(authToken, threadId, formData);

  if (uploadErr) {
    setErrorMessage(controller, CARD_NAME, uploadErr);
    onError?.();
    return;
  }

  const [assets, assetsErr] = await getImageAssets(authToken, threadId);

  if (assetsErr || !assets) {
    setErrorMessage(controller, CARD_NAME, assetsErr);
    onError?.();
    return;
  }

  onSuccess?.(assets);
};

export const HandleDeleteImageAsset: Handler = async (controller, params) => {
  const { threadId, filename, onSuccess, onError } = params;
  const authToken = getAuthToken(controller);

  const [, deleteErr] = await deleteImageAsset(authToken, threadId, filename);

  if (deleteErr) {
    setErrorMessage(controller, CARD_NAME, deleteErr);
    onError?.();
    return;
  }

  const [assets, assetsErr] = await getImageAssets(authToken, threadId);

  if (assetsErr || !assets) {
    setErrorMessage(controller, CARD_NAME, assetsErr);
    onError?.();
    return;
  }

  onSuccess?.(assets);
};

export const HandleGetImageAssets: Handler = async (controller, params) => {
  const { threadId, onSuccess, onError } = params;
  const authToken = getAuthToken(controller);

  const [assets, err] = await getImageAssets(authToken, threadId);

  if (err || !assets) {
    setErrorMessage(controller, CARD_NAME, err);
    onError?.();
    return;
  }

  onSuccess?.(assets);
};

export const HandleGetSegmentVersions: Handler = async (controller, params) => {
  const { threadId, segmentId, assetType, onSuccess, onError, silent } = params;
  const authToken = getAuthToken(controller);

  const [resp, err] = await getSegmentVersions(authToken, threadId, segmentId, assetType);

  // 404 "segment not found" = manifest ยังไม่มี scene นี้
  // (run เก่า / ยังไม่เคย generate / manifest ยังไม่ถูก sync ลง worker) ไม่ใช่ error —
  // ตอบเป็น "ยังไม่มี version" เหมือนที่หลังบ้านทำเมื่อ segment มีแต่ asset ยังไม่มี (200, versions ว่าง)
  if (isNotFound(err)) {
    onSuccess?.(emptySegmentVersions(segmentId, assetType));
    return;
  }

  if (err || !resp) {
    const failure: ApiError = err ?? { status: null, message: "Empty response from version list" };
    if (!silent) setErrorMessage(controller, CARD_NAME, failure);
    onError?.(failure);
    return;
  }

  onSuccess?.(resp);
};

export const HandleSetCurrentVersion: Handler = async (controller, params) => {
  const { threadId, segmentId, assetType, version, onSuccess } = params;
  const authToken = getAuthToken(controller);

  const [, err] = await setCurrentVersion(authToken, threadId, segmentId, assetType, { version });

  if (err) {
    setErrorMessage(controller, CARD_NAME, err);
    return;
  }

  // Refresh state หลัง set version
  await HandleGetDirectScriptState(controller, { threadId });

  if (onSuccess) {
    onSuccess();
  }
};

export const HandleCheckGenerateCost: Handler = async (controller, params) => {
  const { threadId, data, onSuccess, onError } = params;
  const authToken = getAuthToken(controller);

  const [resp, err] = await checkGenerateCost(controller, authToken, threadId, data);

  if (err || !resp) {
    // 200 แต่ body ว่าง → ถือเป็นอาการชั่วคราวเหมือน network (status null) หน้าจอ fallback ได้ตามเดิม
    const failure: ApiError = err ?? { status: null, message: "Empty response from cost check" };
    // 4xx = หลังบ้านปฏิเสธจริง (validation gate / ไม่ใช่ editor / ไม่พบ run) → โชว์ข้อความ
    // network/5xx เงียบ (handleApiError console.error ไว้แล้ว) เพราะหน้าจอจะ fallback ไปสั่ง start ตรง ๆ ซึ่งฟ้อง error เองถ้าล้มจริง
    if (isClientError(failure)) setErrorMessage(controller, CARD_NAME, failure);
    onError?.(failure);
    return;
  }

  onSuccess?.(resp);
};

export const HandleGenerateScene: Handler = async (controller, params) => {
  const { threadId, data, onSuccess } = params;
  const authToken = getAuthToken(controller);

  controller.setState(prev => ({
    MeGenSequence: {
      ...prev.MeGenSequence,
      isJustStartedGenerate: true,
    },
  }));

  const [resp, err] = await startGenerateScene(
    controller,
    authToken,
    threadId,
    {
      ...data,
      render_mode: "review",
      cal_cost: false,
    },
  );

  if (err) {
    setErrorMessage(controller, CARD_NAME, err);

    controller.setState(prev => ({
      MeGenSequence: {
        ...prev.MeGenSequence,
        isJustStartedGenerate: false,
      },
    }));

    return;
  }

  onSuccess?.();
};

export const HandleGetDirectScriptState: Handler = async (controller, params) => {
  const { threadId, onSuccess, onError } = params;
  const authToken = getAuthToken(controller);

  const [
    [stateResp, stateErr],
    [outputResp],
  ] = await Promise.all([
    getDirectScriptState(authToken, threadId),
    getDirectScriptOutput(authToken, threadId),
  ]);

  if (stateErr || !stateResp) {
    failStatePoll(controller, stateErr, { isGenerating: false, isJustStartedGenerate: false }, onError);
    return;
  }

  const state = controller.getState();

  const wasJustStarted = state.MeGenSequence?.isJustStartedGenerate === true;

  const progressKeys = stateResp.progress ? Object.keys(stateResp.progress) : [];

  const isAnyStepRunning = anyStepRunning(stateResp.progress);

  // -- เช็ค progress มีแค่ script2scene steps เท่านั้น
  const hasOnlyScriptToSceneSteps =
    progressKeys.length > 0 &&
    progressKeys.every(key => SCRIPT2SCENE_STEPS.includes(key));

  // -- script2scene_save_result_to_json done = pre-step เสร็จ แต่ยัง draft ไม่ได้
  const isScriptToScenePreStepDone =
    hasOnlyScriptToSceneSteps &&
    stateResp.progress?.["script2scene_save_result_to_json"]?.status === "completed" &&
    !isAnyStepRunning;

  if (isScriptToScenePreStepDone) {
    const clearJustStarted = wasJustStarted ? { isJustStartedGenerate: false }: {};

    if (onSuccess) {
      onSuccess(stateResp, false, outputResp ?? undefined);

      controller.setState({
        MeGenSequence: {
          ...state.MeGenSequence,
          graphState: stateResp as any,
          isGenerating: false,
          ...clearJustStarted,
        },
      });

      return;
    }

    controller.setState({
      MeGenSequence: {
        ...state.MeGenSequence,
        graphState: stateResp as any,
        isGenerating: false,
        ...clearJustStarted,
      },
    });

    return;
  }

  const hasReachedDraftFinalStep = hasAnyStep(stateResp.progress, DRAFT_FINAL_STEPS);
  const hasReachedVdoFinalStep = hasAnyStep(stateResp.progress, VDO_FINAL_STEPS);
  const hasReachedFinalStep = hasReachedDraftFinalStep || hasReachedVdoFinalStep;

  // ถึงบรรทัดนี้ isScriptToScenePreStepDone เป็น false แน่นอน (บล็อกด้านบน return ทุกสาขา)
  const isInTransition = stateResp.next.length === 0
    && !isAnyStepRunning
    && !hasReachedFinalStep;

  const isCompleted = stateResp.next.length === 0
    && stateResp.values
    && Object.keys(stateResp.values).length > 0
    && !isAnyStepRunning
    && !isInTransition;

  const isWaitingUpload = stateResp.next?.includes("upload_to_youtube");

  const hasError = anyStepError(stateResp.progress);

  const allDoneInState = allStepsDone(stateResp.progress);

  const isWorkflowDone = isCompleted || isWaitingUpload || hasError;

  let isGenerating = state.MeGenSequence?.isGenerating || false;

  if (isWorkflowDone) {
    if (wasJustStarted && !hasError) {
      isGenerating = true;
    } else {
      isGenerating = false;
    }
  } else {
    isGenerating = !allDoneInState;
  }

  const clearJustStarted = wasJustStarted ? { isJustStartedGenerate: false } : {};

  if (onSuccess) {
    onSuccess(stateResp, isGenerating, outputResp ?? undefined);

    controller.setState({
      MeGenSequence: {
        ...state.MeGenSequence,
        graphState: stateResp as any,
        isGenerating,
        ...clearJustStarted,
      },
    });

    return;
  }

  controller.setState({
    MeGenSequence: {
      ...state.MeGenSequence,
      graphState: stateResp as any,
      isGenerating,
      ...clearJustStarted,
    },
  });
};

/**========================================================================
 **                        SCRIPT2SCENE HANDLERS
 *========================================================================**/

export const HandleCreateScriptToSceneRun: Handler = async (controller, params) => {
  const { projectId, seriesId, onSuccess } = params;
  const authToken = getAuthToken(controller);

  const createPayload: CreateRunPayload = {
    project_id: projectId,
    series_id: seriesId ?? null,
    type: "script2vdo",
  };

  const [runResp, runErr] = await createRun(authToken, createPayload);

  if (runErr || !runResp) {
    setErrorMessage(controller, CARD_NAME, runErr);

    return;
  }

  controller.setState(prev => ({
    MeGenSequence: {
      ...prev.MeGenSequence,
      currentThreadId: runResp.thread_id,
    },
  }));

  if (onSuccess) {
    onSuccess(runResp.thread_id);
  }
};

export const HandleStartScriptToScene: Handler = async (controller, params) => {
  const { threadId, script, onSuccess } = params;
  const authToken = getAuthToken(controller);

  controller.setState(prev => ({
    MeGenSequence: {
      ...prev.MeGenSequence,
      isGenerating: true,
    },
  }));

  const [resp, err] = await startScriptToScene(authToken, threadId, { script });

  if (err || !resp) {
    setErrorMessage(controller, CARD_NAME, err);

    controller.setState(prev => ({
      MeGenSequence: {
        ...prev.MeGenSequence,
        isGenerating: false,
      },
    }));

    return;
  }

  if (onSuccess) {
    onSuccess(resp.thread_id);
  }
};

export const HandleRefineScriptToScene: Handler = async (controller, params) => {
  const { threadId, data, onSuccess, onError } = params;
  const authToken = getAuthToken(controller);

  const [resp, err] = await refineScriptToScene(authToken, threadId, data);

  if (err || !resp) {
    setErrorMessage(controller, CARD_NAME, err);
    onError?.();

    return;
  }

  setSuccessMessage(controller, CARD_NAME, "Refine script success!");

  if (onSuccess) {
    onSuccess(resp);
  }
};

export const HandleGetScriptToSceneState: Handler = async (controller, params) => {
  const { threadId, onSuccess, onError } = params;
  const authToken = getAuthToken(controller);

  const [stateResp, stateErr] = await getScriptToSceneState(authToken, threadId);

  if (stateErr || !stateResp) {
    failStatePoll(controller, stateErr, { isScriptToSceneGenerating: false }, onError);

    return;
  }

  const isAnyStepRunning = anyStepRunning(stateResp.progress);

  const hasError = anyStepError(stateResp.progress);

  const isCompleted =
    stateResp.next.length === 0 &&
    !!stateResp.values?.raw_scripts_images &&
    !isAnyStepRunning;

  const isWorkflowDone = isCompleted || hasError;
  const isGenerating = !isWorkflowDone;

  controller.setState(prev => ({
    MeGenSequence: {
      ...prev.MeGenSequence,
      isScriptToSceneGenerating: isGenerating,
    },
  }));

  if (onSuccess) {
    onSuccess(stateResp, isGenerating);
  }
};

/**========================================================================
 **                      PROJECT SHARING HANDLERS
 *========================================================================**/

export const HandleSearchUsers: Handler = async (controller, params) => {
  const { query, onSuccess } = params;
  const authToken = getAuthToken(controller);

  const [results, err] = await searchOaUsers(controller, { query });

  if (err || !results) {
    // Silent fail for search - don't show error toast
    console.warn("[MeGen] Search users failed:", err);
    if (onSuccess) onSuccess([]);
    return;
  }

  if (onSuccess) {
    onSuccess(results.items || [] as ProfiledDivisionHasUserSerializer[]);
  }
};

export const HandleGetShareMembers: Handler = async (controller, params) => {
  const { resourceType, projectId, seriesId, threadId, onSuccess } = params;
  const authToken = getAuthToken(controller);

  const shareParams: ShareProjectParams = {
    resource_type: resourceType,
    project_id: projectId,
    series_id: seriesId ?? null,
    thread_id: threadId ?? null,
  };

  const [resp, err] = await getShareMembers(authToken, shareParams);

  if (err || !resp) {
    setErrorMessage(controller, CARD_NAME, err);
    return;
  }

  if (onSuccess) {
    onSuccess(resp);
  }
};

export const HandleShareProject: Handler = async (controller, params) => {
  const { data, onSuccess } = params;
  const authToken = getAuthToken(controller);

  setLoadingStatus(controller, CARD_NAME, true);

  const [, err] = await shareProject(authToken, data);

  setLoadingStatus(controller, CARD_NAME, false);

  if (err) {
    setErrorMessage(controller, CARD_NAME, err);
    return;
  }

  setSuccessMessage(controller, CARD_NAME, "Share project success!");

  if (onSuccess) {
    onSuccess();
  }
};

export const HandleRemoveShareMember: Handler = async (controller, params) => {
  const { data, onSuccess } = params;
  const authToken = getAuthToken(controller);

  const [, err] = await removeShareMember(authToken, data);

  if (err) {
    setErrorMessage(controller, CARD_NAME, err);
    return;
  }

  setSuccessMessage(controller, CARD_NAME, "Remove member success!");

  if (onSuccess) {
    onSuccess();
  }
};

/**========================================================================
 **                            YOUTUBE HANDLERS
 *========================================================================**/

export const HandleConnectYouTube: Handler = async (controller, params) => {
  const { onSuccess, onError } = params;
  const authToken = getAuthToken(controller);

  setLoadingStatus(controller, CARD_NAME, true);

  const [resp, err] = await getYouTubeAuthUrl(authToken);

  setLoadingStatus(controller, CARD_NAME, false);

  if (err || !resp?.authorization_url) {
    setErrorMessage(controller, CARD_NAME, err ?? "Failed to get YouTube auth URL");
    onError?.();
    return;
  }

  setSuccessMessage(controller, CARD_NAME, "Redirecting to YouTube...");
  onSuccess?.(resp.authorization_url);
};

export const HandleGetYouTubeStatus: Handler = async (controller, params) => {
  const { onSuccess } = params;
  const authToken = getAuthToken(controller);

  const [resp, err] = await getYouTubeStatus(authToken);

  if (err || !resp) {
    console.warn("[MeGen] Get YouTube status failed: ", err);
    return;
  }

  controller.setState(prev => ({
    MeGenSequence: {
      ...prev.MeGenSequence,
      youtubeStatus: resp,
    },
  }));

  onSuccess?.(resp);
};

export const HandleDeactivateYouTube: Handler = async (controller, params) => {
  const { channelId, onSuccess, onError } = params;
  const authToken = getAuthToken(controller);

  setLoadingStatus(controller, CARD_NAME, true);

  const [, err] = await deactivateYouTubeChannel(authToken, channelId);

  setLoadingStatus(controller, CARD_NAME, false);

  if (err) {
    setErrorMessage(controller, CARD_NAME, err);
    onError?.();
    return;
  }

  // Refresh status หลัง deactivate
  HandleGetYouTubeStatus(controller, {});
  onSuccess?.();
};

/**========================================================================
 **                         CHARACTER HANDLERS
 *========================================================================**/

export const HandleGetCharacterList: Handler = async (controller, params) => {
  const { seriesId, onSuccess } = params;
  const authToken = getAuthToken(controller);

  setLoadingStatus(controller, CARD_NAME, true);

  const [charactersResp, charactersErr] = await getCharacterList(authToken, seriesId);

  setLoadingStatus(controller, CARD_NAME, false);

  if (charactersErr || !charactersResp) {
    setErrorMessage(controller, CARD_NAME, charactersErr);
    return;
  }

  controller.setState((prev) => ({
    MeGenSequence: {
      ...prev.MeGenSequence,
      characterList: charactersResp,
    },
  }));

  if (onSuccess) {
    onSuccess(charactersResp);
  }
};

export const HandleGenerateCharacter: Handler = async (controller, params) => {
  const { data, onSuccess, onError } = params;
  const authToken = getAuthToken(controller);

  // 1) mark generating
  controller.setState((prev) => ({
    MeGenSequence: { ...prev.MeGenSequence, isCharacterGenerating: true },
  }));

  // 2) Generate
  const [generatedResp, generatedErr] = await generateCharacter(authToken, data);

  if (generatedErr || !generatedResp) {
    controller.setState((prev) => ({
      MeGenSequence: { ...prev.MeGenSequence, isCharacterGenerating: false },
    }));
    setErrorMessage(controller, CARD_NAME, generatedErr);
    if (onError) onError();
    return;
  }

  // 3) Optimistic add — เพิ่ม character ใหม่ใน list ทันที (ยัง unvalidated)
  controller.setState((prev) => ({
    MeGenSequence: {
      ...prev.MeGenSequence,
      characterList: [generatedResp, ...prev.MeGenSequence.characterList],
    },
  }));

  // 4) Auto-validate ทันทีหลัง generate
  const [validatedResp, validateErr] = await validateCharacter(authToken, generatedResp.id);

  controller.setState((prev) => ({
    MeGenSequence: { ...prev.MeGenSequence, isCharacterGenerating: false },
  }));

  if (validateErr || !validatedResp) {
    // validate fail — character ยังอยู่ใน list แต่ยัง unvalidated แจ้ง warning เท่านั้น ไม่ block
    setErrorMessage(controller, CARD_NAME, {
      ...validateErr,
      message: `Character "${generatedResp.name}" ถูกสร้างแล้ว แต่ Auto-validate ล้มเหลว กรุณา Validate เอง`,
    });
    // ยัง callback onSuccess เพราะ generate สำเร็จ
    if (onSuccess) onSuccess(generatedResp);
    return;
  }

  // 5) Replace unvalidated ด้วย validated version ใน list
  controller.setState((prev) => ({
    MeGenSequence: {
      ...prev.MeGenSequence,
      characterList: prev.MeGenSequence.characterList.map((char) =>
        char.id === validatedResp.id ? validatedResp : char
      ),
    },
  }));

  if (onSuccess) onSuccess(validatedResp);
};

export const HandleUploadCharacter: Handler = async (controller, params) => {
  const { data, onSuccess, onError } = params;
  const authToken = getAuthToken(controller);

  controller.setState((prev) => ({
    MeGenSequence: { ...prev.MeGenSequence, isCharacterGenerating: true },
  }));

  const [uploadedResp, uploadedErr] = await uploadCharacter(authToken, data);

  if (uploadedErr || !uploadedResp) {
    controller.setState((prev) => ({
      MeGenSequence: { ...prev.MeGenSequence, isCharacterGenerating: false },
    }));
    setErrorMessage(controller, CARD_NAME, uploadedErr);
    if (onError) onError();
    return;
  }

  const [charactersResp, charactersErr] = await getCharacterList(
    authToken,
    data.series_id
  );

  controller.setState((prev) => ({
    MeGenSequence: {
      ...prev.MeGenSequence,
      isCharacterGenerating: false,
      // ถ้า refetch ล้มเหลว ใช้ optimistic add แทนเพื่อไม่ให้ผู้ใช้เห็นว่าอัปโหลดหาย
      characterList:
        charactersErr || !charactersResp
          ? [uploadedResp, ...prev.MeGenSequence.characterList]
          : charactersResp,
    },
  }));

  if (onSuccess) onSuccess(uploadedResp);
};

export const HandleValidateCharacter: Handler = async (controller, params) => {
  const { characterId, onSuccess, onError } = params;
  const authToken = getAuthToken(controller);

  setLoadingStatus(controller, CARD_NAME, true);

  const [validatedResp, validatedErr] = await validateCharacter(authToken, characterId);

  setLoadingStatus(controller, CARD_NAME, false);

  if (validatedErr || !validatedResp) {
    setErrorMessage(controller, CARD_NAME, validatedErr);
    if (onError) onError();
    return;
  }

  // -- update list
  controller.setState((prev) => ({
    MeGenSequence: {
      ...prev.MeGenSequence,
      characterList: prev.MeGenSequence.characterList.map((char) =>
        char.id === validatedResp.id ? validatedResp : char
      ),
    },
  }));

  if (onSuccess) {
    onSuccess(validatedResp);
  }
};

export const HandleDeleteCharacter: Handler = async (controller, params) => {
  const { characterId, seriesId, onSuccess, onError } = params;
  const authToken = getAuthToken(controller);

  setLoadingStatus(controller, CARD_NAME, true);

  const [, deletedErr] = await deleteCharacter(authToken, characterId);

  setLoadingStatus(controller, CARD_NAME, false);

  if (deletedErr) {
    setErrorMessage(controller, CARD_NAME, deletedErr);
    if (onError) onError();
    return;
  }

  // Optimistic remove — ไม่ต้อง refetch ทั้ง list
  controller.setState((prev) => ({
    MeGenSequence: {
      ...prev.MeGenSequence,
      characterList: prev.MeGenSequence.characterList.filter(
        (char) => char.id !== characterId
      ),
    },
  }));

  if (onSuccess) onSuccess();
};

/**========================================================================
 **                                APIS
 *========================================================================**/

// --- User Profile --- //
export const getUserProfile = async (authToken: string): Promise<UserProfile> => {
  try {
    const response: AxiosResponse<UserProfile> = await axios.get(`${API_URL}/get_user_profile`, {
      headers: { Authorization: authToken },
    });
    return response.data;
  } catch (error) {
    console.error("Failed to fetch user profile", error);
    throw error;
  }
};

/*================================ RUN MANAGEMENT APIS ==============================*/

// --- Create Run --- //
export const createRun = async (
  authToken: string,
  data: CreateRunPayload
): Promise<ApiResponse<RunResponse>> => {
  try {
    const response: AxiosResponse<RunResponse> = await axios.post(
      `${API_URL}/run`,
      data,
      {
        headers: { Authorization: authToken },
      },
    );

    return [response.data, null];
  } catch (error) {
    return [null, handleApiError(error)];
  };
};

// --- Start Run --- //
export const startRun = async (
  controller: Controller,
  authToken: string,
  threadId: string,
  data?: StartRssNewsPayload
): Promise<ApiResponse<void>> => {
  try {
    await axios.post(
      `${API_URL}/run/${threadId}/start`,
      {
        ...data,
        render_mode: "review",
        his_division: controller.data.division,
      },
      {
        headers: { Authorization: authToken },
      },
    );

    return [undefined as void, null];
  } catch (error) {
    return [null, handleApiError(error)];
  };
};

// --- Start Final Run --- //
export const startFinalRun = async (
  authToken: string,
  threadId: string,
  data?: StartRssNewsPayload
): Promise<ApiResponse<void>> => {
  try {
    await axios.post(
      `${API_URL}/run/${threadId}/start`,
      {
        ...data,
        render_mode: "final",
      },
      {
        headers: { Authorization: authToken },
      },
    );

    return [undefined as void, null];
  } catch (error) {
    return [null, handleApiError(error)];
  }
};

// --- State (Detail) Run --- //
export const getRunState = async (
  authToken: string,
  threadId: string,
): Promise<ApiResponse<RunStateSerializer>> => {
  try {
    const response: AxiosResponse<RunStateSerializer> = await axios.get(
      `${API_URL}/run/${threadId}/state`,
      {
        headers: { Authorization: authToken },
      },
    );

    return [response.data, null];
  } catch (error) {
    return [null, handleApiError(error)];
  };
};

// --- Stop Run --- //
export const stopRun = async (
  authToken: string,
  threadId: string
): Promise<ApiResponse<StopRunSerializer>> => {
  try {
    const response: AxiosResponse<StopRunSerializer> = await axios.post(
      `${API_URL}/run/${threadId}/stop`,
      {},
      {
        headers: { Authorization: authToken },
      },
    );

    return [response.data, null];
  } catch (error) {
    return [null, handleApiError(error)];
  };
};

// --- Resume (Upload To YouTube) Run --- //
export const resumeRun = async (
  authToken: string,
  threadId: string,
  credentialId: number | null
): Promise<ApiResponse<void>> => {
  try {
    await axios.post(
      `${API_URL}/run/${threadId}/resume`,
      {
        render_mode: "final", // confirm
        credential_id: credentialId,
      },
      {
        headers: { Authorization: authToken },
      },
    );

    return [undefined as void, null];
  } catch (error) {
    return [null, handleApiError(error)];
  }
};

// --- Rerun (Restart) Run --- //
export const rerunRun = async (
  authToken: string,
  threadId: string,
): Promise<ApiResponse<RerunRunSerializer>> => {
  try {
    const response: AxiosResponse<RerunRunSerializer> = await axios.post(
      `${API_URL}/run/${threadId}/rerun`,
      {
        render_mode: "review", // forced
      },
      {
        headers: { Authorization: authToken },
      },
    );

    return [response.data, null];
  } catch (error) {
    return [null, handleApiError(error)];
  };
};

// --- Delete Run --- //
export const deleteRun = async (
  authToken: string,
  threadId: string
): Promise<ApiResponse<DeleteRunSerializer>> => {
  try {
    const response: AxiosResponse<DeleteRunSerializer> = await axios.delete(
      `${API_URL}/run/${threadId}`,
      {
        headers: { Authorization: authToken },
      },
    );

    return [response.data, null];
  } catch (error) {
    return [null, handleApiError(error)];
  }
};

// --- Duplicate Run --- //
export const duplicateRun = async (
  authToken: string,
  threadId: string
): Promise<ApiResponse<DuplicateRunSerializer>> => {
  try {
    const response: AxiosResponse<DuplicateRunSerializer> = await axios.post(
      `${API_URL}/run/${threadId}/duplicate`,
      {},
      {
        headers: { Authorization: authToken }
      },
    );

    return [response.data, null];
  } catch (error) {
    return [null, handleApiError(error)];
  }
};

// --- Update (Edit) Item (ตอนนี้มีเฉพาะ RSS) Run --- //
export const updateRunItem = async (
  authToken: string,
  threadId: string,
  data: UpdateItemPayload
): Promise<ApiResponse<UpdateItemSerializer>> => {
  try {
    const response: AxiosResponse<UpdateItemSerializer> = await axios.post(
      `${API_URL}/run/${threadId}/update_item`,
      {
        ...data,
        render_mode: "review", // forced
      },
      {
        headers: { Authorization: authToken },
      },
    );

    return [response.data, null];
  } catch (error) {
    return [null, handleApiError(error)];
  };
};

// --- Update (Edit) Metadata (YouTube Info) Run --- //
export const updateRunMetadata = async (
  authToken: string,
  threadId: string,
  data: UpdateMetadataPayload,
): Promise<ApiResponse<UpdateMetadataSerializer>> => {
  try {
    const response: AxiosResponse<UpdateMetadataSerializer> = await axios.post(
      `${API_URL}/run/${threadId}/update_metadata`,
      {
        ...data,
        render_mode: "review",
      },
      {
        headers: { Authorization: authToken },
      }
    );

    return [response.data, null];
  } catch (error) {
    return [null, handleApiError(error)];
  };
};

// --- Update (Edit) Position Item (ตอนนี้มีเฉพาะ RSS) --- //
export const updateComposition = async (
  authToken: string,
  threadId: string,
  data: UpdateCompositionPayload
): Promise<ApiResponse<void>> => {
  try {
    await axios.put(
      `${API_URL}/run/${threadId}/composition`,
      data,
      { headers: { Authorization: authToken } },
    );

    return [undefined as void, null];
  } catch (error) {
    return [null, handleApiError(error)];
  }
};

// --- Run History (List) --- //
/**
 * คืน response ทั้งก้อน (ไม่ใช่แค่ `runs`) เพราะ `total`/`has_more` จำเป็นกับ badge และปุ่ม "โหลดเพิ่ม"
 * `params` ให้ประกอบจาก `buildRunsParams` (common/runsQuery.ts) — อย่าประกอบเอง
 * ไม่ส่ง `limit` มา = ได้ค่า default ของ server (20) ซึ่งมักไม่ใช่สิ่งที่ต้องการ
 */
export const getRuns = async (
  authToken: string,
  params?: Record<string, string | number>,
): Promise<ApiResponse<RunsResponse>> => {
  try {
    const respone: AxiosResponse<RunsResponse> = await axios.get(
      `${API_URL}/runs`,
      {
        headers: { Authorization: authToken },
        ...(params ? { params } : {}),
      },
    );

    return [respone.data, null];
  } catch (error) {
    return [null, handleApiError(error)];
  };
};

// --- Schedule / Settings --- //
export const getSchedule = async (authToken: string): Promise<ScheduleConfig> => {
  try {
    const response: AxiosResponse<ScheduleConfig> = await axios.get(`${API_URL}/schedule`, {
      headers: { Authorization: authToken },
    });
    return response.data;
  } catch (error) {
    console.error("Failed to load schedule", error);
    throw error;
  }
};

/*================================ PROJECT MANAGEMENT APIS ==============================*/

// --- Project List --- //
export const getProjectList = async (
  authToken: string,
  params?: { skip: number; limit: number },
): Promise<ApiResponse<ProjectSerializer[]>> => {
  try {
    const response: AxiosResponse<ProjectSerializer[]> = await axios.get(
      `${API_URL}/projects/`,
      {
        headers: { Authorization: authToken },
        params,
      },
    );
    // Cost paging must not interpret a malformed/null payload as an empty final page.
    if (params && !Array.isArray(response.data)) return [null, { status: null, message: "Invalid project list response" }];
    return [response.data || [], null];
  } catch (error) {
    return [null, handleApiError(error)];
  }
};

// Lightweight thread-to-project metadata; unlike /runs this endpoint is not capped at 200.
export const getProjectRunMaps = async (authToken: string, projectId: number): Promise<ApiResponse<UsageProjectRunMap[]>> => {
  try {
    const response: AxiosResponse<UsageProjectRunMap[]> = await axios.get(`${API_URL}/projects/${projectId}/runmaps`, { headers: { Authorization: authToken } });
    return [response.data, null];
  } catch (error) {
    return [null, handleApiError(error)];
  }
};

// --- Create Project --- //
export const createProject = async (
  authToken: string,
  data: ProjectPayload,
): Promise<ApiResponse<ProjectSerializer>> => {
  try {
    const response: AxiosResponse<ProjectSerializer> = await axios.post(
      `${API_URL}/projects/`,
      data,
      {
        headers: { Authorization: authToken },
      },
    );

    return [response.data, null];
  } catch (error) {
    return [null, handleApiError(error)];
  }
};

// --- Project Detail --- //
export const getProjectDetail = async (
  authToken: string,
  id: number,
): Promise<ApiResponse<ProjectSerializer>> => {
  try {
    const response: AxiosResponse<ProjectSerializer> = await axios.get(
      `${API_URL}/projects/${id}`,
      {
        headers: { Authorization: authToken },
      },
    );

    return [response.data, null];
  } catch (error) {
    return [null, handleApiError(error)];
  }
};

// --- Upadate (Edit) Project --- //
export const updateProject = async (
  authToken: string,
  id: number,
  data: ProjectPayload,
): Promise<ApiResponse<ProjectSerializer>> => {
  try {
    const response: AxiosResponse<ProjectSerializer> = await axios.put(
      `${API_URL}/projects/${id}`,
      data,
      {
        headers: { Authorization: authToken },
      },
    );

    return [response.data, null];
  } catch (error) {
    return [null, handleApiError(error)];
  }
};

// --- Delete Project --- //
export const deleteProject = async (authToken: string, id: number): Promise<ApiResponse<void>> => {
  try {
    await axios.delete(`${API_URL}/projects/${id}`, {
      headers: { Authorization: authToken },
    });

    return [undefined as void, null];
  } catch (error) {
    return [null, handleApiError(error)];
  }
};

/*================================ SHARING MANAGEMENT APIS ==============================*/

// --- Search User In Shared Project --- //
// --- Search User In OA --- //
export const searchOaUsers: Handler = async (
  controller,
  params
) => {
  const { query } = params;

  const [resp, err] = await OfficialAccountUserList.list({
    apiToken: controller.apiToken,
    params: {
      ...(query ? { name: query } : {}),
      limit: LIST_ALL_LIMIT,
    },
    extra: { division: controller.data.division },
  });

  if (err || !resp) return [null, err];

  return [resp as SearchOaUserResponse, null];
};

// --- Shared Member List --- //
export const getShareMembers = async (
  authToken: string,
  params: ShareProjectParams
): Promise<ApiResponse<ShareMemberSerializer[]>> => {
  try {
    const { project_id, ...resParams } = params;

    const response: AxiosResponse<ShareMemberSerializer[]> = await axios.get(
      `${API_URL}/permission/${project_id}`,
      {
        headers: { Authorization: authToken },
        params: resParams,
      }
    );
    return [response.data || [], null];
  } catch (error) {
    return [null, handleApiError(error)];
  }
};

// --- Share Project Action --- //
export const shareProject = async (
  authToken: string,
  data: ShareProjectPayload
): Promise<ApiResponse<void>> => {
  try {
    await axios.post(`${API_URL}/permission/share`, data, {
      headers: { Authorization: authToken },
    });
    return [undefined as void, null];
  } catch (error) {
    return [null, handleApiError(error)];
  }
};

// --- Remove (Delete) Shared Member --- //
export const removeShareMember = async (
  authToken: string,
  data: DeleteSharedMemberPayload
): Promise<ApiResponse<void>> => {
  try {
    await axios.delete(`${API_URL}/permission/share`, {
      headers: { Authorization: authToken },
      data,
    });
    return [undefined as void, null];
  } catch (error) {
    return [null, handleApiError(error)];
  }
};

/*================================ PLAYLIST (Former: SERIES) MANAGEMENT APIS ==============================*/

// --- Playlist List --- //
export const getSeriesList = async (
  authToken: string,
  projectId: number,
): Promise<ApiResponse<SeriesSerializer[]>> => {
  try {
    const response: AxiosResponse<SeriesSerializer[]> = await axios.get(
      `${API_URL}/projects/${projectId}/series/`,
      {
        headers: { Authorization: authToken },
      },
    );

    return [response.data || [], null];
  } catch (error) {
    return [null, handleApiError(error)];
  }
};

// --- Create Playlist --- //
export const createSeries = async (
  authToken: string,
  data: SeriesSerializer,
): Promise<ApiResponse<SeriesSerializer>> => {
  try {
    const response: AxiosResponse<SeriesSerializer> = await axios.post(
      `${API_URL}/projects/${data.project_id}/series/`,
      data,
      {
        headers: { Authorization: authToken },
      },
    );

    return [response.data, null];
  } catch (error) {
    return [null, handleApiError(error)];
  }
};

// --- Playlist Detail --- //
export const getSeriesDetail = async (
  authToken: string,
  projectId: number,
  seriesId: number,
): Promise<ApiResponse<SeriesSerializer>> => {
  try {
    const response: AxiosResponse<SeriesSerializer> = await axios.get(
      `${API_URL}/projects/${projectId}/series/${seriesId}`,
      {
        headers: { Authorization: authToken },
      },
    );

    return [response.data, null];
  } catch (error) {
    return [null, handleApiError(error)];
  }
};

// --- Update (Edit) Playlist --- //
export const updateSeries = async (
  authToken: string,
  projectId: number,
  seriesId: number,
  data: SeriesSerializer,
): Promise<ApiResponse<SeriesSerializer>> => {
  try {
    const response: AxiosResponse<SeriesSerializer> = await axios.put(
      `${API_URL}/projects/${projectId}/series/${seriesId}`,
      data,
      {
        headers: { Authorization: authToken },
      },
    );

    return [response.data, null];
  } catch (error) {
    return [null, handleApiError(error)];
  }
};

// --- Delete Playlist --- //
export const deleteSeries = async (
  authToken: string,
  projectId: number,
  seriesId: number,
  confirm?: boolean,
): Promise<ApiResponse<void>> => {
  try {
    await axios.delete(`${API_URL}/projects/${projectId}/series/${seriesId}`, {
      headers: { Authorization: authToken },
      ...(confirm ? { data: { confirm: true } } : {}),
    });

    return [undefined as void, null];
  } catch (error) {
    return [null, handleApiError(error)];
  }
};

/*================================ STORY2SCENE MANAGEMENT APIS ==============================*/

// --- Start Story --- // *** เริ่มคิด Script
export const startStory = async (
  authToken: string,
  threadId: string,
  data: StartStoryPayload
): Promise<ApiResponse<void>> => {
  try {
    await axios.post(
      `${API_URL}/story2scene/${threadId}/start`,
      data,
      {
        headers: { Authorization: authToken },
      },
    );

    return [undefined as void, null];
  } catch (error) {
    return [null, handleApiError(error)];
  };
};

// --- Submit Parts --- // *** Submit แยก Parts (แยก Script)
export const submitParts = async (
  authToken: string,
  threadId: string,
  data: SubmitPartsPayload
): Promise<ApiResponse<void>> => {
  try {
    await axios.post(
      `${API_URL}/story2scene/${threadId}/start`,
      data,
      {
        headers: { Authorization: authToken },
      },
    );

    return [undefined as void, null];
  } catch (error) {
    return [null, handleApiError(error)];
  };
};

// --- Generate Preview Video --- // *** Generate Preview Video หลัง Submit Parts
export const generateVideoFromScript = async (
  authToken: string,
  threadId: string,
  data: GenerateVideoFromScriptPayload,
): Promise<ApiResponse<void>> => {
  try {
    await axios.post(
      `${API_URL}/run/${threadId}/start`,
      data,
      {
        headers: { Authorization: authToken },
      },
    );

    return [undefined as void, null];
  } catch (error) {
    return [null, handleApiError(error)];
  };
};

export const directGenerate = async (
  authToken: string,
  threadId: string,
  data: DirectGeneratePayload
): Promise<ApiResponse<void>> => {
  try {
    await axios.post(
      `${API_URL}/run/${threadId}/start`,
      data,
      { headers: { Authorization: authToken } }
    );
    return [undefined as void, null];
  } catch (error) {
    return [null, handleApiError(error)];
  }
};

// --- Get State --- // *** State ของ Story2Scene
export const getStory2SceneState = async (
  authToken: string,
  threadId: string
): Promise<ApiResponse<Story2SceneGraphState>> => {
  try {
    const response = await axios.get(
      `${API_URL}/story2scene/${threadId}/state`,
      { headers: { Authorization: authToken } }
    );
    return [response.data, null];
  } catch (error) {
    return [null, handleApiError(error)];
  }
};

/*================================ SCRIPT2VDO (DirectScript) MANAGEMENT APIS ==============================*/

// --- Start DirectScript --- // *** สร้าง Generated Script
export const startDirectScript = async (
  authToken: string,
  threadId: string,
  data: StartDirectScriptPayload
): Promise<ApiResponse<void>> => {
  try {
    await axios.post(
      `${API_URL}/script2vdo/${threadId}/start`,
      data,
      { headers: { Authorization: authToken } }
    );

    return [undefined as void, null];
  } catch (error) {
    return [null, handleApiError(error)];
  }
};

// --- Get State --- // *** State ของ Script2VDO
export const getDirectScriptState = async (
  authToken: string,
  threadId: string
): Promise<ApiResponse<DirectScriptGraphState>> => {
  try {
    const response = await axios.get(
      `${API_URL}/script2vdo/${threadId}/state`,
      { headers: { Authorization: authToken } }
    );
    return [response.data, null];
  } catch (error) {
    return [null, handleApiError(error)];
  }
};

// --- Get Output --- // *** Output Media หลัง Generate Preview Video แล้ว
export const getDirectScriptOutput = async (
  authToken: string,
  threadId: string
): Promise<ApiResponse<DirectScriptOutputResponse>> => {
  try {
    const response: AxiosResponse<DirectScriptOutputResponse> = await axios.get(
      `${API_URL}/script2vdo/${threadId}/output`,
      { headers: { Authorization: authToken } },
    );

    return [response.data, null];
  } catch (error) {
    return [null, handleApiError(error)];
  }
};

// --- Update Run's Title
export const updateDirectScriptTitle = async (
  authToken: string,
  threadId: string,
  data: UpdateTitlePayload
): Promise<ApiResponse<UpdateTitleResponse>> => {
  try {
    const response: AxiosResponse<UpdateTitleResponse> = await axios.post(
      `${API_URL}/script2vdo/${threadId}/update-title`,
      data,
      { headers: { Authorization: authToken } }
    );
    return [response.data, null];
  } catch (error) {
    return [null, handleApiError(error)];
  }
};

// --- Update (Edit) DirectScript --- // *** Auto Save กรณีแก้ Prompt ในช่วย Preview Video
export const updateDirectScript = async (
  authToken: string,
  threadId: string,
  data: FinalScriptData,
): Promise<ApiResponse<void>> => {
  try {
    await axios.post(
      `${API_URL}/script2vdo/${threadId}/update`,
      data,
      { headers: { Authorization: authToken } }
    );

    return [undefined as void, null];
  } catch (error) {
    return [null, handleApiError(error)];
  };
};

// --- Upload Video --- // *** Upload Video ของแต่ละ Segment (Scene) นั้น ๆ
export const uploadDirectVideo = async (
  authToken: string,
  threadId: string,
  itemId: string,
  formData: FormData
): Promise<ApiResponse<{ url: string }>> => {
  try {
    const response: AxiosResponse<{ url: string }> = await axios.post(
      `${API_URL}/media/${threadId}/upload_video?item_id=${itemId}`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: authToken
        },
      }
    );

    return [response.data, null];
  } catch (error) {
    return [null, handleApiError(error)];
  };
};

// --- Upload Image --- // *** Upload Image ของแต่ละ Segment (Scene) นั้น ๆ
export const uploadDirectImage = async (
  authToken: string,
  threadId: string,
  itemId: string,
  formData: FormData
): Promise<ApiResponse<{ url: string }>> => {
  try {
    const respone: AxiosResponse<{ url: string }> = await axios.post(
      `${API_URL}/media/${threadId}/upload_image?item_id=${itemId}`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: authToken,
        },
      }
    );

    return [respone.data, null];
  } catch (error) {
    return [null, handleApiError(error)];
  }
};

export const uploadImageAsset = async (
  authToken: string,
  threadId: string,
  formData: FormData
): Promise<ApiResponse<void>> => {
  try {
    await axios.post(`${API_URL}/media/${threadId}/upload_asset`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: authToken,
      },
    });
    return [undefined as void, null];
  } catch (error) {
    return [null, handleApiError(error)];
  }
};

export const getImageAssets = async (
  authToken: string,
  threadId: string
): Promise<ApiResponse<ImageAsset[]>> => {
  try {
    const response: AxiosResponse<ImageAsset[]> = await axios.get(
      `${API_URL}/media/${threadId}/assets`,
      { headers: { Authorization: authToken } }
    );
    return [response.data, null];
  } catch (error) {
    return [null, handleApiError(error)];
  }
};

export const deleteImageAsset = async (
  authToken: string,
  threadId: string,
  filename: string
): Promise<ApiResponse<void>> => {
  try {
    await axios.delete(
      `${API_URL}/media/${threadId}/assets/${encodeURIComponent(filename)}`,
      { headers: { Authorization: authToken } }
    );
    return [undefined as void, null];
  } catch (error) {
    return [null, handleApiError(error)];
  }
};

// --- Upload Image Overlay (item_id = index ของ image overlay) ---
export const uploadImageOverlay = async (
  authToken: string,
  threadId: string,
  itemId: string,
  formData: FormData
): Promise<ApiResponse<{ url: string }>> => {
  try {
    const response: AxiosResponse<{ url: string }> = await axios.post(
      `${API_URL}/media/${threadId}/upload_overlay?item_id=${itemId}`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: authToken,
        },
      }
    );

    return [response.data, null];
  } catch (error) {
    return [null, handleApiError(error)];
  }
};

// --- Version List (on Segment) --- // *** แสดง Version ของ Media ประเภทนั้น ๆ ตาม Segment (Scene)
export const getSegmentVersions = async (
  authToken: string,
  threadId: string,
  segmentId: string,
  assetType: AssetType
): Promise<ApiResponse<SegmentVersionResponse>> => {
  try {
    const response: AxiosResponse<SegmentVersionResponse> = await axios.get(
      `${API_URL}/version/${threadId}/segment/${segmentId}/${assetType}`,
      { headers: { Authorization: authToken } },
    );

    return [response.data, null];
  } catch (error) {
    return [null, handleApiError(error)];
  }
};

// --- Set Version (on Segment) --- // *** Save Version ของ Media ประเภทนั้น ๆ ตาม Segment (Scene)
export const setCurrentVersion = async (
  authToken: string,
  threadId: string,
  segmentId: string,
  assetType: AssetType,
  data: SetCurrentVersionPayload,
): Promise<ApiResponse<SetCurrentVersionResponse>> => {
  try {
    const response: AxiosResponse<SetCurrentVersionResponse> = await axios.post(
      `${API_URL}/version/${threadId}/segment/${segmentId}/${assetType}/set-current`,
      data,
      { headers: { Authorization: authToken } },
    );
    return [response.data, null];
  } catch (error) {
    return [null, handleApiError(error)];
  }
};

// --- Start Generate Scene (Per-Scene) --- // *** Gen เฉพาะ Scene ที่ระบุ
export const startGenerateScene = async (
  controller: Controller,
  authToken: string,
  threadId: string,
  data: StartGenerateDirectScriptPayload
): Promise<ApiResponse<void>> => {
  try {
    await axios.post(
      `${API_URL}/run/${threadId}/start`,
      {
        ...data,
        cal_cost: false,
        his_division: controller.data.division,
        // generate_item_id มาจาก data แล้ว
      },
      { headers: { Authorization: authToken } }
    );
    return [undefined as void, null];
  } catch (error) {
    return [null, handleApiError(error)];
  }
};

// --- Check Cost Re-generate --- // *** เช็ค Credit เมื่อ Generate Video
export const checkGenerateCost = async (
  controller: Controller,
  authToken: string,
  threadId: string,
  data: StartGenerateDirectScriptPayload
): Promise<ApiResponse<CheckCostResponse>> => {
  try {



    const response = await axios.post(
      `${API_URL}/run/${threadId}/start`,
      {
        ...data,
        render_mode: "review",
        cal_cost: true,
        his_division: controller.data.division,
      },
      { headers: { Authorization: authToken } }
    );
    return [response.data, null];
  } catch (error) {
    // *** cost_calculated มาใน error response ด้วย (status 4xx)
    if (axios.isAxiosError(error) && (error.response?.data as CheckCostResponse)?.cost_details) {
      return [error.response?.data as CheckCostResponse, null];
    }
    return [null, handleApiError(error)];
  }
};

/*================================ SCRIPT2SCENE MANAGEMENT APIS ==============================*/

// --- Start Script2Scene --- // *** เริ่มแตก Scene
export const startScriptToScene = async (
  authToken: string,
  threadId: string,
  data: StartScriptToScenePayload
): Promise<ApiResponse<StartScriptToSceneSerializer>> => {
  try {
    const response: AxiosResponse<StartScriptToSceneSerializer> = await axios.post(
      `${API_URL}/script2scene/${threadId}/start`,
      data,
      { headers: { Authorization: authToken } }
    );

    return [response.data, null];
  } catch (error) {
    return [null, handleApiError(error)];
  }
};

// --- Get State --- // *** State ของ Script2Scene
export const getScriptToSceneState = async (
  authToken: string,
  threadId: string
): Promise<ApiResponse<ScriptToSceneGraphState>> => {
  try {
    const response: AxiosResponse<ScriptToSceneGraphState> = await axios.get(
      `${API_URL}/script2scene/${threadId}/state`,
      { headers: { Authorization: authToken } }
    );

    return [response.data, null];
  } catch (error) {
    return [null, handleApiError(error)];
  }
};

export const refineScriptToScene = async (
  authToken: string,
  threadId: string,
  data: RefineScriptToScenePayload
): Promise<ApiResponse<RefineScriptToSceneResponse>> => {
  try {
    const response: AxiosResponse<RefineScriptToSceneResponse> = await axios.post(
      `${API_URL}/script2scene/${threadId}/refine`,
      data,
      { headers: { Authorization: authToken } }
    );

    return [response.data, null];
  } catch (error) {
    return [null, handleApiError(error)];
  }
};

/*================================ YOUTUBE MANAGEMENT APIS ==============================*/

// --- Auth With Google --- // ***  รับ Auth URL เพื่อเชื่อมต่อ Channel YouTube
export const getYouTubeAuthUrl = async (
  authToken: string
): Promise<ApiResponse<YouTubeAuthResp>> => {
  try {
    const response: AxiosResponse<YouTubeAuthResp> = await axios.get(
      `${API_URL}/auth/youtube`,
      {
        headers: { Authorization: authToken },
        withCredentials: true,
      }
    );

    return [response.data, null];
  } catch (error) {
    return [null, handleApiError(error)];
  }
};

// --- Connected List --- // *** เช็ค Connected Channel
export const getYouTubeStatus = async (
  authToken: string
): Promise<ApiResponse<YouTubeStatusResp>> => {
  try {
    const response: AxiosResponse<YouTubeStatusResp> = await axios.get(
      `${API_URL}/auth/youtube/status`,
      { headers: { Authorization: authToken } }
    );
    const data = response.data;
    const normalized: YouTubeStatusResp = Array.isArray(data)
      ? {
          status: data.some(ch => ch.status === "connected") ? "connected" : "required_youtube",
          channels: data,
        }
      : data;

    return [normalized, null];
  } catch (error) {
    return [null, handleApiError(error)];
  }
};

// --- Disconnect Channel --- // *** เอา Channel YouTube ออก
export const deactivateYouTubeChannel = async (
  authToken: string,
  channelId: string
): Promise<ApiResponse<void>> => {
  try {
    await axios.post(
      `${API_URL}/auth/youtube/deactivate`,
      { id: channelId },
      { headers: { Authorization: authToken } }
    );
    return [undefined as void, null];
  } catch (error) {
    return [null, handleApiError(error)];
  }
};

/*================================ CHARACTERS MANAGEMENT APIS ==============================*/

// --- Character List --- // *** ดึงรายการ Character ทั้งหมดใน Series
export const getCharacterList = async (
  authToken: string,
  seriesId: number
): Promise<ApiResponse<CharacterSerializer[]>> => {
  try {
    const response: AxiosResponse<CharacterSerializer[]> = await axios.get(
      `${API_URL}/characters/`,
      {
        headers: { Authorization: authToken },
        params: { series_id: seriesId },
      }
    );

    return [response.data, null];
  } catch (error) {
    return [null, handleApiError(error)];
  }
};

// --- Generate Character ---
export const generateCharacter = async (
  authToken: string,
  data: GenerateCharacterPayload
): Promise<ApiResponse<CharacterSerializer>> => {
  try {
    const response: AxiosResponse<CharacterSerializer> = await axios.post(
      `${API_URL}/characters/generate`,
      data,
      { headers: { Authorization: authToken } }
    );

    return [response.data, null];
  } catch (error) {
    return [null, handleApiError(error)];
  }
};

// --- Upload Character (multipart/form-data) ---
export const uploadCharacter = async (
  authToken: string,
  data: UploadCharacterPayload
): Promise<ApiResponse<CharacterSerializer>> => {
  try {
    const formData = new FormData();
    formData.append("series_id", String(data.series_id));
    formData.append("name", data.name);
    formData.append("file", data.file);
    if (data.description) {
      formData.append("description", data.description);
    }

    const response: AxiosResponse<CharacterSerializer> = await axios.post(
      `${API_URL}/characters/upload`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: authToken,
        },
      }
    );

    return [response.data, null];
  } catch (error) {
    return [null, handleApiError(error)];
  }
};

// --- Validate Character ---
export const validateCharacter = async (
  authToken: string,
  characterId: number
): Promise<ApiResponse<CharacterSerializer>> => {
  try {
    const response: AxiosResponse<CharacterSerializer> = await axios.post(
      `${API_URL}/characters/${characterId}/validate`,
      {},
      { headers: { Authorization: authToken } }
    );

    return [response.data, null];
  } catch (error) {
    return [null, handleApiError(error)];
  }
};

// --- Delete Character ---
export const deleteCharacter = async (
  authToken: string,
  characterId: number
): Promise<ApiResponse<DeleteCharacterResponse>> => {
  try {
    const response: AxiosResponse<DeleteCharacterResponse> = await axios.delete(
      `${API_URL}/characters/${characterId}`,
      { headers: { Authorization: authToken } }
    );

    return [response.data, null];
  } catch (error) {
    return [null, handleApiError(error)];
  }
};

/*================================ MEDIA CONFIG APIS ==============================*/

// --- Get Media Styles --- //
export const getMediaStyles = async (
  authToken: string
): Promise<ApiResponse<MediaStyle[]>> => {
  try {
    const response: AxiosResponse<MediaStyle[]> = await axios.get(
      `${API_URL}/media/styles`,
      { headers: { Authorization: authToken } }
    );

    return [response.data, null];
  } catch (error) {
    return [null, handleApiError(error)];
  }
};

// --- Get Media Voices --- //
export const getVoiceList = async (
  authToken: string
): Promise<ApiResponse<VoiceSerializer[]>> => {
  try {
    const response: AxiosResponse<VoiceSerializer[]> = await axios.get(
      `${API_URL}/media/voices`,
      { headers: { Authorization: authToken } }
    );
    return [response.data, null];
  } catch (error) {
    return [null, handleApiError(error)];
  }
};

// --- Get Image Model Catalog --- //
export const getImageModels = async (
  authToken: string
): Promise<ApiResponse<ImageModelCatalog>> => {
  try {
    const response: AxiosResponse<ImageModelCatalog> = await axios.get(
      `${API_URL}/media/image-models`,
      { headers: { Authorization: authToken } }
    );
    return [response.data, null];
  } catch (error) {
    return [null, handleApiError(error)];
  }
};

// --- Get Video Model Catalog --- //
export const getVideoModels = async (
  authToken: string
): Promise<ApiResponse<VideoModelCatalog>> => {
  try {
    const response: AxiosResponse<VideoModelCatalog> = await axios.get(
      `${API_URL}/media/video-models`,
      { headers: { Authorization: authToken } }
    );
    return [response.data, null];
  } catch (error) {
    return [null, handleApiError(error)];
  }
};

/**========================================================================
 **                            ERROR HANDLERS
 *========================================================================**/

const handleApiError = (error: unknown): ApiError => {
  console.error("[MeGen] API Error: ", error);

  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<ApiErrorResponse>;

    if (axiosError.response) {
      const data = axiosError.response.data;

      return {
        status: axiosError.response.status,
        message:
          data?.message ||
          data?.error ||
          data?.detail ||
          axiosError.response.statusText ||
          axiosError.message,
        data: data,
      };
    }

    if (axiosError.request) {
      return {
        status: null,
        message: "No response from server. Please check your connection.",
      };
    }

    return {
      status: null,
      message: axiosError.message,
    };
  }

  return {
    status: null,
    message: error instanceof Error ? error.message : "Unknown error occurred",
  };
};

/**========================================================================
 **                            HANDLER HELPERS
 *========================================================================**/

const setLoadingStatus = (
  controller: Controller,
  key: keyof PickedState["loadingStatus"],
  loading: boolean,
) => {
  controller.setState((prev) => ({
    loadingStatus: {
      ...prev.loadingStatus,
      [key]: loading,
    },
  }));
};

/** จุดเดียวที่ error ทุกตัวผ่านก่อนถึง snack — เก็บเป็น string เสมอ (errorText) ไม่เก็บ ApiError ทั้งก้อน
 *  เพราะ ErrorMessage.tsx จะ render object เป็นแถว status/message/data ดิบ ๆ */
const setErrorMessage = (
  controller: Controller,
  key: keyof PickedState["errorMessage"],
  error: unknown,
) => {
  controller.setState((prev) => ({
    errorMessage: {
      ...prev.errorMessage,
      [key]: errorText(error),
    },
  }));
};

const setSuccessMessage = (
  controller: Controller,
  key: keyof PickedState["successMessage"],
  message: any,
) => {
  controller.setState((prev) => ({
    successMessage: {
      ...prev.successMessage,
      [key]: message,
    },
  }));
};

/**
 * error ตอน poll state (useRunPolling ยิงทุก 30 วิ และไม่หยุดตอนสลับแท็บเพราะ MainHIS keepMounted)
 * - network/5xx = อาการชั่วคราว → เงียบ (handleApiError console.error ไว้แล้ว) รอบถัดไปลองเอง
 *   ไม่งั้นหลังบ้านสะดุดทีเดียว = snack เด้งซ้ำทุก 30 วิ
 * - 4xx = คำตอบชัดเจน (run ถูกลบ / ไม่มีสิทธิ์) → โชว์ครั้งเดียว แล้วปิด flag ที่ค้ำ polling ไว้
 *   ไม่งั้นวนถามซ้ำตลอดไป
 * - flag ที่ค้ำ polling ไม่ได้อยู่ใน MeGenSequence ทั้งหมด: การ์ด direct script มี flag ของตัวเอง
 *   (isGeneratingScene / isScriptToSceneGenerating) ที่ล้างเฉพาะใน onSuccess ⇒ ต้องบอกการ์ดผ่าน
 *   `onError` ของ params ด้วย ไม่งั้น 4xx ค้าง ๆ ยังยิงต่อและ snack เด้งซ้ำทุก 30 วิ
 */
const failStatePoll = (
  controller: Controller,
  err: ApiError | null,
  stopFlags: Partial<State["MeGenSequence"]>,
  onError?: (err: ApiError) => void,
) => {
  if (!isClientError(err) || !err) return;

  setErrorMessage(controller, CARD_NAME, err);
  controller.setState((prev) => ({
    MeGenSequence: { ...prev.MeGenSequence, ...stopFlags },
  }));
  onError?.(err);
};

const getAuthToken = (controller: Controller): string => {
  return `Token ${controller.apiToken}`;
};

/*================================ AI USAGE (MeGenCost) ==============================*/

// --- Records (raw rows ทุก user — แหล่งข้อมูลของ dashboard) --- //
export const getAiUsageRecords = async (
  authToken: string,
  params: { start_date?: string; end_date?: string; thread_id?: string; limit?: number; offset?: number }
): Promise<ApiResponse<AiUsageRecordsResponse>> => {
  try {
    const response: AxiosResponse<AiUsageRecordsResponse> = await axios.get(`${API_URL}/ai-usage/records`, {
      params,
      headers: { Authorization: authToken },
    });

    return [response.data, null];
  } catch (error) {
    return [null, handleApiError(error)];
  }
};

export const getFfmpegUsageTasks = async (
  authToken: string,
  params: { start_date?: string; end_date?: string; thread_id?: string; limit?: number; offset?: number }
): Promise<ApiResponse<FfmpegUsageResponse>> => {
  try {
    const response: AxiosResponse<FfmpegUsageResponse> = await axios.get(`${API_URL}/internal/ffmpeg-tasks/list`, {
      params: { ...params, task_type: "ffmpeg" },
      headers: { Authorization: authToken },
    });
    return [response.data, null];
  } catch (error) {
    return [null, handleApiError(error)];
  }
};

// --- Thread Detail (totals + by_model + timeline) --- //
export const getAiUsageThreadDetail = async (
  authToken: string,
  threadId: string
): Promise<ApiResponse<AiUsageThreadDetail>> => {
  try {
    const response: AxiosResponse<AiUsageThreadDetail> = await axios.get(
      `${API_URL}/ai-usage/thread/${threadId}`,
      { headers: { Authorization: authToken } }
    );
    // The thread endpoint enforces run visibility before this second source is requested.
    const tasks = await loadAllFfmpegUsageTasks(async (limit, offset) => {
      const [page, error] = await getFfmpegUsageTasks(authToken, { thread_id: threadId, limit, offset });
      if (error || !page) throw new AiUsageLoadError("request", error?.message || "FFmpeg thread usage request failed");
      return page;
    }, () => true, () => {});
    const userNames = { ...response.data.user_names };
    tasks.forEach((task) => { if (task.user_id && task.user_name) userNames[task.user_id] = task.user_name; });
    return [{ ...response.data, ffmpeg_tasks: tasks, user_names: userNames,
      users: Array.from(new Set([...response.data.users, ...tasks.flatMap((task) => task.user_id ? [task.user_id] : [])])),
    }, null];
  } catch (error) {
    return [null, handleApiError(error)];
  }
};

// --- Thread Titles (ชื่อ video ต่อ thread — batch ละ <=100 id) --- //
export const getAiUsageThreadTitles = async (
  authToken: string,
  threadIds: string[]
): Promise<ApiResponse<{ titles: Record<string, string> }>> => {
  try {
    const response: AxiosResponse<{ titles: Record<string, string> }> = await axios.get(
      `${API_URL}/ai-usage/thread-titles`,
      {
        params: { thread_ids: threadIds.join(",") },
        headers: { Authorization: authToken },
      }
    );

    return [response.data, null];
  } catch (error) {
    return [null, handleApiError(error)];
  }
};

const aiUsageRequests = new WeakMap<Controller, { active: boolean }>();
const aiUsageProjectRequests = new WeakMap<Controller, object>();

export const HandleGetAiUsageProjects: Handler = async (controller) => {
  const authToken = getAuthToken(controller);
  const request = {};
  aiUsageProjectRequests.set(controller, request);
  const isCurrent = () => aiUsageProjectRequests.get(controller) === request && getAuthToken(controller) === authToken;
  // Separate from the ordinary menu projectList and from date-range record/title requests.
  controller.setState((prev) => isCurrent() ? { MeGenSequence: { ...prev.MeGenSequence, aiUsageProjectIndex: undefined, aiUsageProjectError: undefined, aiUsageProjectProgress: { projects: 0, completed: 0, total: null, loading: true } } } : null);
  try {
    const index = await loadUsageProjectIndex(async (skip, limit) => {
      const [projects, error] = await getProjectList(authToken, { skip, limit });
      if (error || !projects) throw new UsageProjectLoadError("request");
      return projects.map(({ id, name }) => ({ id, name }));
    }, async (projectId) => {
      const [rows, error] = await getProjectRunMaps(authToken, projectId);
      if (error || !rows) throw new UsageProjectLoadError("request");
      return rows;
    }, isCurrent, (progress) => controller.setState((prev) => isCurrent() ? { MeGenSequence: { ...prev.MeGenSequence, aiUsageProjectProgress: progress } } : null));
    if (isCurrent()) controller.setState((prev) => isCurrent() ? { MeGenSequence: { ...prev.MeGenSequence, aiUsageProjectIndex: index } } : null);
  } catch (error) {
    if (!isCurrent()) return;
    const reason = error instanceof UsageProjectLoadError ? error.reason : "request";
    controller.setState((prev) => isCurrent() ? { MeGenSequence: { ...prev.MeGenSequence, aiUsageProjectError: reason, aiUsageProjectProgress: prev.MeGenSequence.aiUsageProjectProgress ? { ...prev.MeGenSequence.aiUsageProjectProgress, loading: false } : undefined } } : null);
  }
};

export const HandleCancelAiUsageRecords: Handler = (controller) => {
  aiUsageProjectRequests.delete(controller);
  controller.setState((prev) => aiUsageProjectRequests.has(controller) || !prev.MeGenSequence.aiUsageProjectProgress?.loading ? null : { MeGenSequence: { ...prev.MeGenSequence, aiUsageProjectError: "cancelled", aiUsageProjectProgress: { ...prev.MeGenSequence.aiUsageProjectProgress, loading: false } } });
  if (!aiUsageRequests.get(controller)?.active) return;
  aiUsageRequests.delete(controller);
  controller.setState((prev) => aiUsageRequests.has(controller) ? null : ({ MeGenSequence: { ...prev.MeGenSequence, aiUsageLoading: false, aiUsageLoadError: "cancelled", aiUsageTitleProgress: prev.MeGenSequence.aiUsageTitleProgress ? { ...prev.MeGenSequence.aiUsageTitleProgress, loading: false } : undefined } }));
};

export const HandleGetAiUsageRecords: Handler = async (controller, params) => {
  const { startDate, endDate } = params;
  const authToken = getAuthToken(controller);
  const request = { active: true };
  aiUsageRequests.set(controller, request);
  const isCurrent = () => aiUsageRequests.get(controller) === request && request.active && getAuthToken(controller) === authToken;
  controller.setState((prev) => isCurrent() ? ({
    MeGenSequence: { ...prev.MeGenSequence, aiUsageLoading: true, aiUsageLoadError: undefined, aiUsageProgress: { loaded: 0, total: null }, aiUsageTitleProgress: undefined },
  }) : null);
  let range: AiUsageRange;
  let allRecords: AiUsageRecordRow[];
  try {
    range = aiUsageRangeOf(startDate, endDate);
    // Start the independent metadata load alongside usage paging; never block verified totals.
    void HandleGetAiUsageProjects(controller);
    allRecords = await loadAllUsageSources(async (limit, offset) => {
      const [resp, err] = await getAiUsageRecords(authToken, { start_date: range.startIso, end_date: range.endIso, limit, offset });
      if (err || !resp) throw new AiUsageLoadError("request", err?.message || "Usage request failed");
      return resp;
    }, async (limit, offset) => {
      const [resp, err] = await getFfmpegUsageTasks(authToken, { start_date: range.startIso, end_date: range.endIso, limit, offset });
      if (err || !resp) throw new AiUsageLoadError("request", err?.message || "FFmpeg usage request failed");
      return resp;
    }, isCurrent, (progress) => controller.setState((prev) => isCurrent() ? { MeGenSequence: { ...prev.MeGenSequence, aiUsageProgress: progress } } : null));
  } catch (error) {
    if (!isCurrent()) return;
    const reason = error instanceof AiUsageLoadError ? error.reason : "request";
    controller.setState((prev) => isCurrent() ? ({ MeGenSequence: { ...prev.MeGenSequence, aiUsageLoading: false, aiUsageRecordsTruncated: true, aiUsageLoadError: reason } }) : null);
    request.active = false;
    return;
  }
  if (!isCurrent()) return;

  controller.setState((prev) => isCurrent() ? ({
    MeGenSequence: {
      ...prev.MeGenSequence,
      aiUsageRecords: allRecords,
      aiUsageAppliedRange: range,
      aiUsageRecordsTruncated: false,
      aiUsageLoading: false,
    },
  }) : null);

  await loadAiUsageTitles(controller, allRecords, isCurrent, authToken);
};

export const HandleGetAiUsageTitles: Handler = async (controller) => {
  if (controller.getState().MeGenSequence.aiUsageLoading) return;
  const request = { active: true };
  aiUsageRequests.set(controller, request);
  await loadAiUsageTitles(controller, controller.getState().MeGenSequence.aiUsageRecords || [], () => aiUsageRequests.get(controller) === request, getAuthToken(controller));
};

const loadAiUsageTitles = async (controller: Controller, allRecords: AiUsageRecordRow[], isCurrent: () => boolean, authToken: string) => {

  // --- ข้อมูลประกอบการแสดงผล (พลาดได้เงียบ ๆ — UI fallback เป็น id) ---
  const distinctThreadIds = Array.from(
    new Set(allRecords.map((r) => r.thread_id).filter(Boolean))
  ) as string[];

  const cache = controller.getState().MeGenSequence.aiUsageThreadTitles || {};
  // A sequential full index preserves title search while bounding concurrency to one.
  await loadAiUsageTitleIndex(distinctThreadIds, cache, async (ids) => {
    const [resp, err] = await getAiUsageThreadTitles(authToken, ids);
    return err || !resp ? null : resp.titles;
  }, isCurrent, (titles, progress) => controller.setState((prev) => isCurrent() ? { MeGenSequence: { ...prev.MeGenSequence, aiUsageThreadTitles: { ...prev.MeGenSequence.aiUsageThreadTitles, ...titles }, aiUsageTitleProgress: progress } } : null));
  if (!isCurrent()) return;
  if (!(controller.getState().MeGenSequence?.allOaUsersList || []).length) {
    const [resp] = await searchOaUsers(controller, { query: "" });
    if (isCurrent() && resp?.items) controller.setState((prev) => isCurrent() ? ({ MeGenSequence: { ...prev.MeGenSequence, allOaUsersList: resp.items } }) : null);
  }
  if (isCurrent()) aiUsageRequests.get(controller)!.active = false;
};

export const HandleGetAiUsageThreadDetail: Handler = async (controller, params) => {
  const { threadId, onSuccess, onError } = params;
  const authToken = getAuthToken(controller);

  const [resp, err] = await getAiUsageThreadDetail(authToken, threadId);

  if (err || !resp) {
    const message =
      err?.status === HTTP_FORBIDDEN
        ? "ไม่มีสิทธิ์ดูข้อมูลการใช้งานของ run นี้"
        : err?.status === HTTP_NOT_FOUND
          ? "ไม่พบข้อมูลการใช้งานของ run นี้"
          : err?.message || "โหลดข้อมูลการใช้งานไม่สำเร็จ";

    onError?.(message);
    return;
  }

  onSuccess?.(resp);
};

/**========================================================================
 **                                HELPERS
 *========================================================================**/

// URL ที่เป็น absolute อยู่แล้ว (เช่น character image จาก prod host) ห้ามเติม API_URL ซ้ำ
const isAbsoluteUrl = (url: string): boolean =>
  /^(https?:)?\/\//i.test(url) || /^(data|blob):/i.test(url);

// --- Common Static --- //
export const getStaticUrl = (path: string): string | null => {
  if (!path) return null;
  if (path.includes("/data/")) {
    return `${API_URL}/static` + path.split("/data")[1];
  }
  if (path.includes("/podcast_data/")) {
    return `${API_URL}/static` + path.split("/podcast_data/")[1];
  }
  return null;
};

// --- Story2Scene Static --- //
export const getStoryStaticUrl = (path: string): string | null => {
  if (!path) return null;
  if (path.includes("/data/")) {
    return `${API_URL}/static/` + path.split("/data")[1];
  }
  if (path.includes("/podcast_data/")) {
    return `${API_URL}/static/` + path.split("/podcast_data/")[1];
  }
  return null;
};

// --- RSS Static --- //
export const getStaticUrlNews = (path: string): string | null => {
  if (!path) return null;
  if (path.includes("/data/")) {
    return `${API_URL}/static` + path.split("/data")[1];
  }
  if (path.includes("/podcast_data/")) {
    return `${API_URL}/static` + path.split("/podcast_data")[1];
  }
  return null;
};

// --- Script2Vdo Static --- //
export const getDirectStaticUrl = (threadId: string | null, filename: string): string => {
  if (!threadId) return "";

  if (isAbsoluteUrl(filename)) return filename;

  if (filename.includes("/static")) {
    const path = filename.split("/static")[1];

    return `${API_URL}/static/${path}`;
  }

  return `${API_URL}/static/${threadId}/${filename}`;
};

export const getUploadedStaticUrl = (uploadedUrl: string | null | undefined) => {
  if (!uploadedUrl) return;
  if (isAbsoluteUrl(uploadedUrl)) return uploadedUrl;
  return API_URL.concat(uploadedUrl);
};

export const getOutputStaticUrl = (path: string | null | undefined) => {
  if (!path) return "";
  if (isAbsoluteUrl(path)) return path;
  return `${API_URL}${path}`;
};

// --- Media Cache Buster --- //
export const getCacheBusterStaticUrl = (url: string) => {
  const cacheBuster = Date.now();

  return url.concat(`${url.includes("?") ? "&" : "?"}t=${cacheBuster}`);
};
